import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { Check, X, ChevronDown, ChevronUp, Scissors, AlertCircle, Shield, Sparkles, ArrowRight, Search, Ban, MapPin, Lock, Pencil, Plus, Trash2, Calendar, LayoutList, LayoutGrid, Undo2, Eye, EyeOff, Copy } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogBody } from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Input } from '../ui/input';
import { newSelect as NewSelect } from '../ui/newSelect';
import { Actions as IMPORT_ACTIONS } from '../../store/actions/intelligentImportActions';
import { Actions as USAGE_ACTIONS } from '../../store/actions/usageActions';
import { Actions as PAYMENT_ACTIONS } from '../../store/actions/paymentActions';
import { QUALITY_TIERS, QUALITY_LEVELS, AUTO_CONFIRM_THRESHOLD, STAGING_ROW_STATUSES } from '../../constants/importConstants';
import { qualityTierStringToLevel } from '../../utils/qualityHelpers';

const QualityBadge = ({ level }) => {
  const tier = QUALITY_TIERS[level];
  if (!tier) return null;
  return <Badge className={`${tier.color} bg-transparent border`}>{tier.label}</Badge>;
};

const ConfidenceBar = ({ confidence }) => {
  const pct = Math.round((confidence || 0) * 100);
  const color = pct >= 85 ? 'bg-success' : pct >= 60 ? 'bg-warning' : 'bg-danger';
  return (
    <div className='flex items-center gap-2'>
      <div className='flex-1 h-2 bg-muted rounded-full overflow-hidden max-w-[80px]'>
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className='text-xs font-mono text-muted-foreground'>{pct}%</span>
    </div>
  );
};

const computeEmissions = (amount, co2ePerUnit) => {
  if (!amount || !co2ePerUnit) return null;
  const val = parseFloat(amount) * parseFloat(co2ePerUnit);
  return isNaN(val) ? null : Math.round(val * 10) / 10;
};

const pickFirst = (obj, ...keys) => {
  if (!obj) return null;
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    const v = obj[k];
    if (v != null && v !== '') return v;
  }
  return null;
};

const parseNum = (v) => {
  if (v == null || v === '') return null;
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : n;
};

const normalizeCurrencyCode = (raw) => {
  const s = String(raw || '').trim().toUpperCase();
  if (s === '£' || s === 'GBP') return 'GBP';
  if (s === '$' || s === 'USD') return 'USD';
  if (s === '€' || s === 'EUR') return 'EUR';
  const letters = s.replace(/[^A-Z]/g, '');
  return letters || 'GBP';
};

/** ISO 4217-style codes the API may send as `detected_unit` for spend-first rows (e.g. gbp, usd). */
const ISO4217_CURRENCY_CODES = new Set(
  'GBP USD EUR AUD CAD CHF JPY SEK NOK DKK PLN CZK HUF RON BGN TRY RUB BRL MXN ZAR NZD SGD HKD INR KRW CNY ILS AED SAR THB MYR IDR PHP VND CLP COP ARS PEN EGP NGN KES GHS PKR BDT LKR TWD UAH UYU BOB PYG CRC DOP'.split(
    ' ',
  ),
);

const isCurrencyDetectedUnit = (raw) => {
  if (raw == null || raw === '') return false;
  const t = String(raw).trim();
  if (['£', '$', '€', '¥', '￥'].includes(t)) return true;
  const letters = t.toUpperCase().replace(/[^A-Z]/g, '');
  if (letters.length !== 3) return false;
  const nonCurrencyThreeLetter = new Set(['KWH', 'MWH', 'GWH', 'KM', 'KG', 'M2', 'M3', 'GJ']);
  if (nonCurrencyThreeLetter.has(letters)) return false;
  return ISO4217_CURRENCY_CODES.has(letters);
};

const formatOriginalFinancialContext = (row) => {
  if (!row) return null;
  const ccy = normalizeCurrencyCode(row.currency || row.detectedCurrency);
  const amt = row.amount;
  if (amt == null || amt === '') return null;
  return `Original: ${ccy} ${fmt2dp(amt)}`;
};

const symbolForCurrencyCode = (code) => {
  if (code === 'GBP') return '£';
  if (code === 'USD') return '$';
  if (code === 'EUR') return '€';
  return `${code} `;
};

const getSpendFactorCurrency = (match, metricsById) => {
  const fd = match?.factorDimensions || match?.factor_dimensions || {};
  const fromDims = fd.spendCurrency || fd.spend_currency || fd.factorCurrency || fd.factor_currency;
  if (fromDims) return normalizeCurrencyCode(fromDims);
  const label = getMetricLabel(match, metricsById);
  const name = `${match?.factorName || ''} ${match?.factor_name || ''}`;
  const blob = `${label} ${name}`.toLowerCase();
  if (/\$\s*spend| usd|kgco2e\/\$|\/\$\s*spend/.test(blob)) return 'USD';
  if (/£\s*spend| gbp|kgco2e\/£|\/£\s*spend/.test(blob)) return 'GBP';
  if (/€\s*spend| eur|kgco2e\/€/.test(blob)) return 'EUR';
  if (label.includes('$')) return 'USD';
  if (label.includes('£')) return 'GBP';
  if (label.includes('€')) return 'EUR';
  return null;
};

/**
 * Spend amount in the same currency as the factor's spend denominator (backend conversions preferred).
 * @returns {{ amount: number|null, currencyCode: string, source: string, hint?: string }}
 */
const resolveSpendForFactor = (row, match, metricsById) => {
  const factorCcy = getSpendFactorCurrency(match, metricsById);
  const rowAmt = parseNum(row?.amount);
  const rowCcy = normalizeCurrencyCode(row?.currency || row?.detectedCurrency || 'GBP');

  const backendGbp = parseNum(
    pickFirst(
      row,
      'amountGbp',
      'amount_gbp',
      'gbpAmount',
      'gbp_amount',
      'spendAmountGbp',
      'spend_amount_gbp',
      'reportingAmountGbp',
      'reporting_amount_gbp',
    ),
  );
  const backendUsd = parseNum(
    pickFirst(row, 'amountUsd', 'amount_usd', 'usdAmount', 'usd_amount', 'spendAmountUsd', 'spend_amount_usd'),
  );
  const backendEur = parseNum(
    pickFirst(row, 'amountEur', 'amount_eur', 'eurAmount', 'eur_amount', 'spendAmountEur', 'spend_amount_eur'),
  );

  const matchUsd = parseNum(pickFirst(match || {}, 'spendAmountUsd', 'spend_amount_usd'));
  const matchGbp = parseNum(pickFirst(match || {}, 'spendAmountGbp', 'spend_amount_gbp'));
  const matchEur = parseNum(pickFirst(match || {}, 'spendAmountEur', 'spend_amount_eur'));

  if (factorCcy === 'USD') {
    if (matchUsd != null) return { amount: matchUsd, currencyCode: 'USD', source: 'match' };
    if (backendUsd != null) return { amount: backendUsd, currencyCode: 'USD', source: 'backend' };
    if (rowCcy === 'USD' && rowAmt != null) return { amount: rowAmt, currencyCode: 'USD', source: 'row' };
    return { amount: null, currencyCode: 'USD', source: 'missing', hint: 'usd' };
  }
  if (factorCcy === 'GBP') {
    if (matchGbp != null) return { amount: matchGbp, currencyCode: 'GBP', source: 'match' };
    if (backendGbp != null) return { amount: backendGbp, currencyCode: 'GBP', source: 'backend' };
    if (rowCcy === 'GBP' && rowAmt != null) return { amount: rowAmt, currencyCode: 'GBP', source: 'row' };
    return { amount: null, currencyCode: 'GBP', source: 'missing', hint: 'gbp' };
  }
  if (factorCcy === 'EUR') {
    if (matchEur != null) return { amount: matchEur, currencyCode: 'EUR', source: 'match' };
    if (backendEur != null) return { amount: backendEur, currencyCode: 'EUR', source: 'backend' };
    if (rowCcy === 'EUR' && rowAmt != null) return { amount: rowAmt, currencyCode: 'EUR', source: 'row' };
    return { amount: null, currencyCode: 'EUR', source: 'missing', hint: 'eur' };
  }

  if (backendGbp != null) return { amount: backendGbp, currencyCode: 'GBP', source: 'backend' };
  return { amount: rowAmt, currencyCode: rowCcy, source: 'row' };
};

const computeSpendEmissions = (row, match, metricsById) => {
  if (!match || !isSpendFactor(match, metricsById)) return computeEmissions(row?.amount, match?.co2ePerUnit);
  const sp = resolveSpendForFactor(row, match, metricsById);
  if (sp.amount == null) return null;
  return computeEmissions(sp.amount, match.co2ePerUnit);
};

const fmt2dp = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? v : n.toFixed(2);
};

const UNIT_FAMILY_LABELS = {
  spend: '£', energy: 'kWh', volume: 'litres', mass: 'kg',
  distance: 'km', area: 'm²',
};

/** Resolves the unit label for an activity_upgrade object.
 * Numeric metric IDs (e.g. "4385") are resolved through metricsById to their name (e.g. "kWh").
 * Non-numeric strings from the backend (e.g. "tonne.km") are returned directly.
 * Falls back to getMetricLabel(topMatch) which can extract the unit from the factor name. */
const resolveUpgradeUnitLabel = (upgrade, topMatch, metricsById) => {
  const ut = upgrade?.unitType || upgrade?.unit_type;
  if (ut) {
    const fromMap = metricsById[String(ut)];
    if (fromMap) return fromMap;
    if (!/^\d+$/.test(String(ut))) return ut;
  }
  const upgradeName = upgrade?.factorName || upgrade?.factor_name || upgrade?.factor_display_name;
  if (upgradeName) {
    const fromUpgrade = getMetricLabel({ factorName: upgradeName }, metricsById);
    if (fromUpgrade && fromUpgrade !== 'units') return fromUpgrade;
  }
  return getMetricLabel(topMatch, metricsById);
};

const getMetricLabel = (match, metricsById = {}) => {
  const metricId = match?.metric || match?.unit || '';
  if (metricId && metricsById[String(metricId)]) return metricsById[String(metricId)];
  if (metricId && !/^\d+$/.test(metricId) && !/^[a-f0-9-]{20,}$/i.test(metricId)) return metricId;
  const family = match?.unitFamily || match?.unit_family || '';
  if (UNIT_FAMILY_LABELS[family]) return UNIT_FAMILY_LABELS[family];
  const dims = match?.factorDimensions || {};
  if (dims.unit && !/^\d+$/.test(dims.unit)) return dims.unit;
  const name = match?.factorName || match?.factor_name || match?.factor_display_name || '';
  const dotParts = name.split(' · ');
  if (dotParts.length > 1) {
    const last = dotParts[dotParts.length - 1].trim();
    if (last && last.length < 30) return last;
  }
  return 'units';
};

const isSpendFactor = (match, metricsById) => {
  const fd = match?.factorDimensions || match?.factor_dimensions || {};
  const unitFamily = match?.unitFamily || match?.unit_family || fd.unit_family;
  if (unitFamily) return unitFamily === 'spend';

  const displayName = (match?.factorName || match?.factor_name || match?.factor_display_name || '').trim();
  const nameParts = displayName.split('·').map((p) => p.trim());
  const lastNameSeg = nameParts[nameParts.length - 1] || '';

  const activityUnits = /\b(kwh|kWh|m3|m²|m2|km|kg|tonne|tonnes|litre|litres|gallon|gallons|mile|miles|passenger|ton|unit|units|MWh|GJ|GWh|therm|therms)\b/i;
  if (activityUnits.test(lastNameSeg)) return false;

  if (/\bSpend\b/i.test(lastNameSeg)) return true;

  const metricLabel = metricsById ? getMetricLabel(match, metricsById) : '';
  if (activityUnits.test(metricLabel)) return false;
  if (/\bSpend\b/i.test(metricLabel)) return true;

  const metric = match?.metric || match?.unit || '';
  return /^[£$€]/.test(metric) || metric === 'GBP' || metric === 'USD' || metric === 'EUR';
};

/** Activity quantity from match/row when `detected_unit` is not a spend currency (ignore e.g. 290 + gbp for kWh factors). */
const getEffectiveActivityDetectedAmount = (match, row, metricsById) => {
  if (!match || isSpendFactor(match, metricsById)) return null;
  const du = pickFirst(match, 'detectedUnit', 'detected_unit') || pickFirst(row, 'detectedUnit', 'detected_unit');
  if (isCurrencyDetectedUnit(du)) return null;
  const amt = match.detectedAmount ?? match.detected_amount;
  if (amt === undefined || amt === null || amt === '') return null;
  return amt;
};

const qualityBadgeLevelForMatch = (match, row) => {
  const fromTier = qualityTierStringToLevel(pickFirst(match, 'qualityTier', 'quality_tier'));
  if (fromTier != null) return fromTier;
  return row?.qualityLevel ?? match?.qualityLevel ?? QUALITY_LEVELS.SPEND;
};

const candidateToMatch = (candidate, row) => {
  const fd = candidate.factorDimensions || candidate.factor_dimensions || {};
  return {
    factorId: candidate.factorId || candidate.factor_id || candidate.id,
    factorName:
      candidate.factor_display_name ||
      candidate.factorName ||
      candidate.factor_name ||
      candidate.name,
    entities: candidate.entities || [],
    confidence: parseFloat(candidate.signals?.llm_confidence ?? candidate.signals?.formula_score ?? candidate.confidence ?? 0) || 0,
    co2ePerUnit: parseFloat(candidate.co2ePerUnit || candidate.co2e_per_unit || fd.co2e_per_unit || 0) || 0,
    unitFamily: candidate.unitFamily || candidate.unit_family || fd.unit_family,
    metric: candidate.metric || fd.metric,
    unit: candidate.unit || fd.unit,
    factorDimensions: fd,
    qualityTier: candidate.qualityTier || candidate.quality_tier,
    quality_tier: candidate.quality_tier || candidate.qualityTier,
    detectedAmount: candidate.detectedAmount ?? candidate.detected_amount ?? row?.detectedAmount ?? row?.detected_amount,
    detected_amount: candidate.detected_amount ?? candidate.detectedAmount ?? row?.detected_amount ?? row?.detectedAmount,
    detectedUnit: candidate.detectedUnit ?? candidate.detected_unit ?? pickFirst(row, 'detectedUnit', 'detected_unit'),
    detected_unit: candidate.detected_unit ?? candidate.detectedUnit ?? pickFirst(row, 'detected_unit', 'detectedUnit'),
    subentity: candidate.subentity,
  };
};

const EXCLUDE_REASONS = [
  { value: 'CANNOT_DETERMINE_SOURCE', label: 'Cannot determine emission source (e.g. ATM withdrawal)' },
  { value: 'INTERNAL_TRANSFER', label: 'Internal transfer / non-purchase transaction' },
  { value: 'BELOW_MATERIALITY', label: 'Below materiality threshold' },
  { value: 'DUPLICATE_ENTRY', label: 'Duplicate of another entry' },
  { value: 'PERSONAL_EXPENSE', label: 'Personal expense — not business related' },
  { value: 'OTHER', label: 'Other (specify below)' },
];

const REJECT_REASONS = [
  { value: 'wrong_category', label: 'Wrong category' },
  { value: 'wrong_unit', label: 'Wrong unit / currency' },
  { value: 'not_relevant', label: 'Not relevant to this transaction' },
  { value: 'too_broad', label: 'Too broad / generic' },
  { value: 'other', label: 'Other' },
];

const getDedupContext = (row) => {
  const raw = row?.dedupContext || row?.dedup_context;
  if (raw && typeof raw === 'object') return raw;
  return null;
};

/** A dedup override re-matches the row in the background; /matches has nothing for it for a few seconds. */
const DEDUP_REMATCH_RETRY_DELAYS_MS = [2000, 2500, 3000, 4000, 5000, 5000];

const delay = (ms) => new Promise((resolve) => { window.setTimeout(resolve, ms); });

const matchesIncludeRow = (matches, rowId) =>
  (Array.isArray(matches) ? matches : []).some((m) => String(m.rowId || m.stagingId || m.id) === String(rowId));

const getDedupScope = (row) => {
  const ctx = getDedupContext(row);
  if (ctx?.scope) return ctx.scope;
  if (row?.dedupScope) return row.dedupScope;
  if (row?.dedup_scope) return row.dedup_scope;
  return null;
};

const parseActivityUpgrade = (match) => {
  const raw = match?.activityUpgrade || match?.activity_upgrade;
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return null; }
};

const parseSpendFallback = (match) => {
  const raw = match?.spendFallback || match?.spend_fallback;
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return null; }
};

const parsePremiumUpgrade = (match) => {
  const raw = match?.premiumUpgrade || match?.premium_upgrade;
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return null; }
};

const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const inferPeriodBoundsFromLabel = (label) => {
  if (!label || typeof label !== 'string') return { start: '', end: '' };
  const t = label.trim();
  if (ISO_DATE_ONLY.test(t)) return { start: t, end: t };
  return { start: '', end: '' };
};

const normaliseIsoDateish = (v) => {
  if (v == null || v === '') return '';
  const s = String(v).trim();
  if (ISO_DATE_ONLY.test(s)) return s;
  const slice = s.slice(0, 10);
  return ISO_DATE_ONLY.test(slice) ? slice : '';
};

const resolveDefaultFacilityId = (facilities) => {
  if (!facilities?.length) return '';
  const primary = facilities.find((f) => f.isPrimary === true || f.isPrimary === 'yes');
  if (primary) return String(primary.id);
  return String(facilities[0].id);
};

/**
 * Resolved facility for confirm UI: explicit override, else name match to org list, else primary/first facility.
 */
const resolveStagingFacilityId = (rowId, matchedFacilityName, facilityOverrides, orgFacilities) => {
  const o = facilityOverrides[rowId];
  if (o !== undefined && o !== null && String(o).trim() !== '') return String(o);
  if (matchedFacilityName && orgFacilities?.length) {
    const name = String(matchedFacilityName).trim();
    const exact = orgFacilities.find((f) => String(f.name).trim() === name);
    if (exact) return String(exact.id);
    const ci = orgFacilities.find((f) => String(f.name).trim().toLowerCase() === name.toLowerCase());
    if (ci) return String(ci.id);
  }
  return resolveDefaultFacilityId(orgFacilities);
};

const FactorMatchReview = ({
  importId,
  job,
  factorMatches,
  stagingItems,
  loading,
  getFactorMatches,
  getStagingItems,
  confirmMatch,
  rejectMatch,
  unconfirmMatch,
  bulkConfirmHighConfidence,
  commitImport,
  recordAuditEvent,
  excludeRow,
  searchFactors,
  suggestFactorFromText,
  submitFactorSuggestFeedback,
  splitStagingRow,
  retryMatch,
  overrideDedupSkip,
  orgCountry,
  currentJob,
  orgMetrics,
  orgFacilities,
  cyfAdmin,
  whiteLabelAdmin,
  addonCatalog,
  addonCatalogLoading,
  addonPurchaseLoading,
  getAddonCatalog,
  purchaseAddOn,
}) => {
  const [overrideModal, setOverrideModal] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideNotes, setOverrideNotes] = useState('');
  const [processing, setProcessing] = useState({});
  const [rejectedFactors, setRejectedFactors] = useState({});
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('wrong_category');
  const [rememberChoice, setRememberChoice] = useState(true);
  const [excludeModal, setExcludeModal] = useState(null);
  const [excludeReason, setExcludeReason] = useState(EXCLUDE_REASONS[0].value);
  const [excludeNotes, setExcludeNotes] = useState('');
  const [rememberExclusion, setRememberExclusion] = useState(false);
  const [searchModal, setSearchModal] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [aiSuggestResponse, setAiSuggestResponse] = useState(null);
  const [aiSuggestLoading, setAiSuggestLoading] = useState(false);
  const [clarifyReplies, setClarifyReplies] = useState({});
  const [alternatesOpen, setAlternatesOpen] = useState(false);
  const [locationOverride, setLocationOverride] = useState(null);
  const [searchModalActivity, setSearchModalActivity] = useState('');
  const [searchModalActivityError, setSearchModalActivityError] = useState('');
  const [activityRequiredBanner, setActivityRequiredBanner] = useState('');
  const [selectedEntities, setSelectedEntities] = useState({});
  const [entityPickerRow, setEntityPickerRow] = useState(null);
  const [splitModal, setSplitModal] = useState(null);
  const [splitLines, setSplitLines] = useState([]);
  const [activityAmounts, setActivityAmounts] = useState({});
  const [viewMode, setViewMode] = useState('detailed');
  const [compactExpandedKey, setCompactExpandedKey] = useState(null);
  const compactAccordionRef = useRef(null);
  const [showCompletedRows, setShowCompletedRows] = useState(false);
  const [showDuplicateRows, setShowDuplicateRows] = useState(false);
  const [upgradeAmounts, setUpgradeAmounts] = useState({});
  const [altActivityAmounts, setAltActivityAmounts] = useState({});
  const [alternatesOpenRows, setAlternatesOpenRows] = useState({});
  const [splitInlineRow, setSplitInlineRow] = useState(null);
  const [splitGroups, setSplitGroups] = useState({});
  const [splitChildEdits, setSplitChildEdits] = useState({});
  const [periodOverrides, setPeriodOverrides] = useState({});
  const [facilityOverrides, setFacilityOverrides] = useState({});
  const [matchSuccessFlash, setMatchSuccessFlash] = useState({});
  const [upgradeDialog, setUpgradeDialog] = useState(null);
  const [upgradeBillingType, setUpgradeBillingType] = useState('annual');
  const [rematchingRows, setRematchingRows] = useState({});
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const flashRowMatched = useCallback((rowId) => {
    const id = String(rowId);
    setMatchSuccessFlash((prev) => ({ ...prev, [id]: true }));
    window.setTimeout(() => {
      setMatchSuccessFlash((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }, 750);
  }, []);

  const metricsById = useMemo(() => {
    const map = {};
    (orgMetrics || []).forEach((m) => { map[String(m.id)] = m.name; });
    return map;
  }, [orgMetrics]);

  const facilityOptions = useMemo(
    () => (orgFacilities || []).map((f) => ({ value: String(f.id), label: f.name })),
    [orgFacilities],
  );

  const openUpgradeDialog = useCallback((addOnId, addOnName) => {
    if (!addonCatalog?.length) getAddonCatalog();
    setUpgradeDialog({ addOnId, addOnName });
    setUpgradeBillingType('annual');
  }, [addonCatalog, getAddonCatalog]);

  const findProductForAddOn = useCallback(
    (addOnId) => (addonCatalog || []).find((p) => (p.addon_ids || []).includes(addOnId)),
    [addonCatalog],
  );

  const handleUpgradePurchase = useCallback(async () => {
    if (!upgradeDialog?.addOnId) return;
    const product = findProductForAddOn(upgradeDialog.addOnId);
    if (!product) return;
    const result = await purchaseAddOn(product.id, upgradeBillingType);
    if (result?.success) {
      setUpgradeDialog(null);
      if (importId) {
        getFactorMatches(importId);
        getStagingItems(importId);
      }
    }
  }, [upgradeDialog, upgradeBillingType, purchaseAddOn, importId, getFactorMatches, getStagingItems, findProductForAddOn]);

  useEffect(() => {
    if (importId) {
      getFactorMatches(importId);
      getStagingItems(importId);
    }
  }, [importId]);

  useEffect(() => {
    const rowById = {};
    (stagingItems || []).forEach((r) => {
      rowById[String(r.id || r.stagingId)] = r;
    });
    const prefills = {};
    (Array.isArray(factorMatches) ? factorMatches : []).forEach((m) => {
      if (!m.isTopMatch) return;
      const key = m.rowId || m.stagingId || m.id;
      const row = rowById[String(key)];
      const eff = getEffectiveActivityDetectedAmount(m, row, metricsById);
      if (eff != null && !activityAmounts[key]) {
        prefills[key] = String(eff);
      }
    });
    if (Object.keys(prefills).length) {
      setActivityAmounts((prev) => ({ ...prev, ...prefills }));
    }
  }, [factorMatches, metricsById, stagingItems]);

  useEffect(() => {
    if (!searchModal) return;
    const du = pickFirst(searchModal, 'detectedUnit', 'detected_unit');
    const raw = searchModal.detectedAmount ?? searchModal.detected_amount;
    const seed = !isCurrencyDetectedUnit(du) && raw != null && raw !== '' ? String(raw) : '';
    setSearchModalActivity(seed);
    setSearchModalActivityError('');
  }, [searchModal]);

  useEffect(() => {
    if (!activityRequiredBanner) return;
    const t = setTimeout(() => setActivityRequiredBanner(''), 6000);
    return () => clearTimeout(t);
  }, [activityRequiredBanner]);

  useEffect(() => {
    if (!compactExpandedKey || viewMode !== 'compact') return;
    const id = window.requestAnimationFrame(() => {
      compactAccordionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [compactExpandedKey, viewMode]);

  const matchMap = useMemo(() => {
    const map = {};
    (Array.isArray(factorMatches) ? factorMatches : []).forEach((m) => {
      const key = m.rowId || m.stagingId || m.id;
      const rejected = rejectedFactors[key] || [];
      if (rejected.includes(m.factorId)) return;
      if (!map[key]) map[key] = [];
      map[key].push(m);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => (b.isTopMatch ? 1 : 0) - (a.isTopMatch ? 1 : 0)));
    return map;
  }, [factorMatches, rejectedFactors]);

  const splitParentRows = useMemo(() => {
    const map = {};
    stagingItems.forEach((row) => {
      const key = row.id || row.stagingId;
      const children = row.splitChildren || row.split_children;
      if ((Array.isArray(children) && children.length > 0) || (row.pipelineStatus || row.status) === 'SPLIT') {
        map[key] = row;
      }
    });
    return map;
  }, [stagingItems]);

  const duplicateSkippedCount = useMemo(
    () => stagingItems.filter((r) => (r.pipelineStatus || r.status) === STAGING_ROW_STATUSES.DUPLICATE_SKIPPED).length,
    [stagingItems],
  );

  const reviewRows = useMemo(() => {
    const filtered = stagingItems.filter((r) => {
      const status = r.pipelineStatus || r.status;
      if (status === STAGING_ROW_STATUSES.DUPLICATE_SKIPPED) return showDuplicateRows;
      if (!['PENDING_REVIEW', 'PENDING_MATCH', 'CONFIRMED', 'EXCLUDED', 'COMMIT_ERROR', 'COMMITTED', STAGING_ROW_STATUSES.DEDUP_OVERRIDDEN].includes(status)) return false;
      const key = r.id || r.stagingId;
      if (splitParentRows[key]) return false;
      return true;
    });
    return filtered.sort((a, b) => {
      const da = a.transactionDate || a.transaction_date || a.date || '';
      const db = b.transactionDate || b.transaction_date || b.date || '';
      if (da < db) return -1;
      if (da > db) return 1;
      const descA = (a.cleanDescription || a.rawDescription || a.description || '').toLowerCase();
      const descB = (b.cleanDescription || b.rawDescription || b.description || '').toLowerCase();
      return descA.localeCompare(descB);
    });
  }, [stagingItems, splitParentRows, showDuplicateRows]);

  const rowsForListView = useMemo(() => {
    if (showCompletedRows) return reviewRows;
    return reviewRows.filter((r) => {
      const s = r.pipelineStatus || r.status;
      return !['CONFIRMED', 'COMMITTED'].includes(s);
    });
  }, [reviewRows, showCompletedRows]);

  const splitParentMap = useMemo(() => {
    const parentMap = {};
    stagingItems.forEach((row) => {
      const pid = row.parentStagingId || row.parentId;
      if (pid) parentMap[row.id || row.stagingId] = pid;
    });
    Object.entries(splitParentRows).forEach(([parentId, parentRow]) => {
      const children = parentRow.splitChildren || parentRow.split_children || [];
      children.forEach((cid) => { if (!parentMap[cid]) parentMap[cid] = parentId; });
    });
    Object.entries(splitGroups).forEach(([parentId, group]) => {
      (group.childIds || []).forEach((cid) => { if (!parentMap[cid]) parentMap[cid] = parentId; });
    });
    return parentMap;
  }, [stagingItems, splitParentRows, splitGroups]);

  const groupedReviewRows = useMemo(() => {
    const groups = [];
    const parentIdx = {};
    const processed = new Set();

    rowsForListView.forEach((row) => {
      const key = row.id || row.stagingId;
      if (processed.has(key)) return;
      const parentId = splitParentMap[key];
      if (parentId) {
        if (parentIdx[parentId] === undefined) {
          parentIdx[parentId] = groups.length;
          const parentRow = splitParentRows[parentId];
          const frontendInfo = splitGroups[parentId];
          groups.push({
            type: 'split-group',
            parentId,
            parentDesc: parentRow?.cleanDescription || parentRow?.rawDescription || parentRow?.description || frontendInfo?.description || '',
            parentAmount: parentRow?.amount || frontendInfo?.amount,
            parentCurrency: parentRow?.currency || frontendInfo?.currency || '£',
            parentDate: parentRow?.transactionDate || parentRow?.transaction_date || parentRow?.date || frontendInfo?.date,
            children: [row],
          });
        } else {
          groups[parentIdx[parentId]].children.push(row);
        }
        processed.add(key);
      } else {
        groups.push({ type: 'row', row });
        processed.add(key);
      }
    });
    return groups;
  }, [rowsForListView, splitParentMap, splitParentRows, splitGroups]);

  const stats = useMemo(() => {
    const total = reviewRows.length;
    const confirmed = reviewRows.filter((r) => (r.pipelineStatus || r.status) === 'CONFIRMED').length;
    const committed = reviewRows.filter((r) => (r.pipelineStatus || r.status) === 'COMMITTED').length;
    const commitErrors = reviewRows.filter((r) => (r.pipelineStatus || r.status) === 'COMMIT_ERROR').length;
    const highConf = reviewRows.filter((r) => {
      const matches = matchMap[r.id || r.stagingId] || [];
      return matches.some((m) => (m.confidence || 0) >= AUTO_CONFIRM_THRESHOLD && m.status !== 'CONFIRMED');
    }).length;
    const excluded = reviewRows.filter((r) => (r.pipelineStatus || r.status) === 'EXCLUDED').length;
    const exhausted = reviewRows.filter((r) => {
      const key = r.id || r.stagingId;
      const rowStatus = r.pipelineStatus || r.status;
      return rowStatus !== 'EXCLUDED' && (matchMap[key] || []).length === 0 && (rejectedFactors[key] || []).length > 0;
    }).length;
    return { total, confirmed, committed, commitErrors, pending: total - confirmed - excluded - committed - commitErrors, highConf, exhausted, excluded };
  }, [reviewRows, matchMap, rejectedFactors]);

  const handleConfirm = useCallback(async (row, match, remember = rememberChoice, entityId, activityAmountOverride) => {
    const rowId = row.id || row.stagingId;
    const factorId = match.factorId || match.factor_id;
    const factorName = match.factorName || match.factor_name;
    const entities = match.entities || [];
    const description = row.cleanDescription || row.rawDescription || '';
    const nonSpend = !isSpendFactor(match, metricsById);

    if (entities.length > 1 && !entityId) {
      const preChosen = selectedEntities[rowId];
      const preChosenValid = preChosen && entities.some((e) => String(e.id) === String(preChosen));
      if (preChosenValid) {
        entityId = preChosen;
      } else {
        setEntityPickerRow({ row, match, selectOnly: false });
        setSelectedEntities((prev) => ({ ...prev, [rowId]: prev[rowId] || entities[0]?.id }));
        return;
      }
    }

    const resolvedEntityId = entityId || entities[0]?.id;
    const extra = {};
    if (nonSpend) {
      const eff = getEffectiveActivityDetectedAmount(match, row, metricsById);
      const amtRaw = activityAmountOverride ?? activityAmounts[rowId] ?? eff;
      if (amtRaw === undefined || amtRaw === null || String(amtRaw).trim() === '' || Number.isNaN(parseFloat(amtRaw))) {
        setActivityRequiredBanner('Enter an activity amount (e.g. tonnes, kWh) for this factor before confirming.');
        return false;
      }
      extra.activityAmount = parseFloat(amtRaw);
      extra.activityUnit = getMetricLabel(match, metricsById);
    }

    const po = periodOverrides[rowId];
    const detPeriodStart = pickFirst(row, 'detectedPeriodStart', 'detected_period_start') || pickFirst(match, 'detectedPeriodStart', 'detected_period_start');
    const detPeriodEnd = pickFirst(row, 'detectedPeriodEnd', 'detected_period_end') || pickFirst(match, 'detectedPeriodEnd', 'detected_period_end');
    const detPeriodLabel = pickFirst(row, 'detectedPeriodLabel', 'detected_period_label') || pickFirst(match, 'detectedPeriodLabel', 'detected_period_label');
    const txnDateRaw = row.transactionDate || row.transaction_date || row.date;
    const inferred = inferPeriodBoundsFromLabel(detPeriodLabel);
    const effectiveStart =
      detPeriodStart ||
      inferred.start ||
      normaliseIsoDateish(txnDateRaw);
    const effectiveEnd = detPeriodEnd || inferred.end || effectiveStart;
    const finalPeriodStart = po?.start || effectiveStart;
    const finalPeriodEnd = po?.end || effectiveEnd;
    if (finalPeriodStart) extra.periodStart = finalPeriodStart;
    if (finalPeriodEnd) extra.periodEnd = finalPeriodEnd;

    const matchedFacName =
      pickFirst(row, 'matchedFacility', 'matched_facility') || pickFirst(match, 'matchedFacility', 'matched_facility');
    const finalFacilityId = resolveStagingFacilityId(rowId, matchedFacName, facilityOverrides, orgFacilities);
    if (finalFacilityId) extra.facilityId = finalFacilityId;

    setProcessing((p) => ({ ...p, [rowId]: true }));
    const confirmResult = await confirmMatch(
      importId,
      rowId,
      factorId,
      factorName,
      resolvedEntityId,
      Object.keys(extra).length ? extra : undefined,
    );
    if (confirmResult == null) {
      setProcessing((p) => ({ ...p, [rowId]: false }));
      return false;
    }
    flashRowMatched(rowId);
    if (remember && description && factorId && submitFactorSuggestFeedback) {
      submitFactorSuggestFeedback({
        text: description,
        factor_id: factorId,
        factor_name: factorName,
        remember_choice: true,
        confidence_score: match.confidence,
      });
    }
    if (recordAuditEvent) {
      recordAuditEvent(importId, 'FACTOR_OVERRIDE', rowId, {
        factorId,
        factorName,
        entityId: resolvedEntityId,
        description,
        remembered: remember,
        confidence: match.confidence,
      });
    }
    await Promise.all([getFactorMatches(importId, { silent: true }), getStagingItems(importId, { silent: true })]);
    setProcessing((p) => ({ ...p, [rowId]: false }));
    return true;
  }, [
    importId,
    confirmMatch,
    getFactorMatches,
    getStagingItems,
    rememberChoice,
    submitFactorSuggestFeedback,
    recordAuditEvent,
    selectedEntities,
    activityAmounts,
    metricsById,
    periodOverrides,
    facilityOverrides,
    orgFacilities,
    flashRowMatched,
  ]);

  const openEntityCategoryPicker = useCallback((row, match) => {
    const rowId = row.id || row.stagingId;
    const entities = match.entities || [];
    if (entities.length < 2) return;
    setEntityPickerRow({ row, match, selectOnly: true });
    setSelectedEntities((prev) => {
      const current = prev[rowId];
      const valid = current && entities.some((e) => String(e.id) === String(current));
      return { ...prev, [rowId]: valid ? current : entities[0]?.id };
    });
  }, []);

  const handleReject = useCallback(async (row, match) => {
    setRejectModal({ row, match });
    setRejectReason('wrong_category');
  }, []);

  const handleRejectConfirm = useCallback(async () => {
    if (!rejectModal) return;
    const { row, match } = rejectModal;
    const rowId = row.id || row.stagingId;
    const factorId = match.factorId || match.factor_id;
    setProcessing((p) => ({ ...p, [rowId]: true }));
    setRejectedFactors((prev) => ({
      ...prev,
      [rowId]: [...(prev[rowId] || []), factorId],
    }));
    await rejectMatch(importId, rowId, factorId, match.factorName || match.factor_name, rejectReason);
    if (recordAuditEvent) {
      recordAuditEvent(importId, 'FACTOR_OVERRIDE', rowId, {
        action: 'REJECTED',
        factorId,
        factorName: match.factorName || match.factor_name,
        reason: rejectReason,
        description: row.cleanDescription || row.rawDescription,
      });
    }
    setProcessing((p) => ({ ...p, [rowId]: false }));
    setRejectModal(null);
  }, [rejectModal, rejectReason, importId, rejectMatch, recordAuditEvent]);

  const handleUnconfirm = useCallback(async (row) => {
    const rowId = row.id || row.stagingId;
    if (!unconfirmMatch) return;
    setProcessing((p) => ({ ...p, [rowId]: true }));
    const result = await unconfirmMatch(importId, rowId);
    setProcessing((p) => ({ ...p, [rowId]: false }));
    if (result) {
      await Promise.all([getFactorMatches(importId, { silent: true }), getStagingItems(importId, { silent: true })]);
    }
  }, [importId, unconfirmMatch, getFactorMatches, getStagingItems]);

  const handleDedupOverride = useCallback(
    async (row) => {
      const rowId = row.id || row.stagingId;
      setProcessing((p) => ({ ...p, [rowId]: true }));
      try {
        const ok = await overrideDedupSkip(importId, rowId);
        if (!ok) return;
        setRematchingRows((prev) => ({ ...prev, [String(rowId)]: true }));
        // The PATCH has already moved the row out of DUPLICATE_SKIPPED, so staging is current straight away.
        await getStagingItems(importId, { silent: true });
        // Matching runs in the background and /matches answers with an empty list until it writes the row,
        // so the fetch is repeated rather than done once (an immediate call would blank every other row).
        for (let i = 0; i < DEDUP_REMATCH_RETRY_DELAYS_MS.length; i += 1) {
          await delay(DEDUP_REMATCH_RETRY_DELAYS_MS[i]);
          if (!mountedRef.current) return;
          const matches = await getFactorMatches(importId, { silent: true });
          if (matchesIncludeRow(matches, rowId)) return;
        }
      } finally {
        setProcessing((p) => ({ ...p, [rowId]: false }));
        setRematchingRows((prev) => {
          const next = { ...prev };
          delete next[String(rowId)];
          return next;
        });
      }
    },
    [importId, overrideDedupSkip, getFactorMatches, getStagingItems],
  );

  const handleBulkConfirm = useCallback(async () => {
    setProcessing((p) => ({ ...p, bulk: true }));
    await bulkConfirmHighConfidence(importId, AUTO_CONFIRM_THRESHOLD);
    await Promise.all([getFactorMatches(importId, { silent: true }), getStagingItems(importId, { silent: true })]);
    setProcessing((p) => ({ ...p, bulk: false }));
  }, [importId, bulkConfirmHighConfidence, getFactorMatches, getStagingItems]);

  const handleProceed = useCallback(async () => {
    await commitImport(importId);
  }, [importId, commitImport]);

  const handleExclude = useCallback(async () => {
    if (!excludeModal) return;
    const rowId = excludeModal.id || excludeModal.stagingId;
    setProcessing((p) => ({ ...p, [rowId]: true }));

    const result = await excludeRow(importId, rowId, {
      reason: excludeReason,
      notes: excludeNotes || undefined,
      remember: rememberExclusion,
    });

    if (result) {
      await Promise.all([getStagingItems(importId, { silent: true }), getFactorMatches(importId, { silent: true })]);
    }

    setProcessing((p) => ({ ...p, [rowId]: false }));
    setExcludeModal(null);
    setExcludeReason(EXCLUDE_REASONS[0].value);
    setExcludeNotes('');
    setRememberExclusion(false);
  }, [excludeModal, excludeReason, excludeNotes, rememberExclusion, importId, excludeRow, getStagingItems, getFactorMatches]);

  const openSplitModal = useCallback((row) => {
    setSplitModal(row);
    setSplitLines([
      { description: '', amount: '', date: '' },
      { description: '', amount: '', date: '' },
    ]);
  }, []);

  const handleSplitConfirm = useCallback(async () => {
    const rowId = splitInlineRow || (splitModal && (splitModal.id || splitModal.stagingId));
    if (!rowId || !splitStagingRow) return;
    const parentRow = stagingItems.find((r) => (r.id || r.stagingId) === rowId);
    const subRows = splitLines
      .filter((l) => l.description.trim() && l.amount)
      .map((l) => ({
        description: l.description.trim(),
        amount: parseFloat(l.amount) || 0,
        ...(l.date ? { date: l.date } : {}),
      }));
    if (subRows.length < 2) return;
    setProcessing((p) => ({ ...p, [rowId]: true }));
    const result = await splitStagingRow(importId, rowId, subRows);
    if (result) {
      const childRows = result.rows || result.subRows || (Array.isArray(result) ? result : []);
      const childIds = childRows.map((r) => r.id || r.stagingId).filter(Boolean);
      if (parentRow) {
        setSplitGroups((prev) => ({
          ...prev,
          [rowId]: {
            description: parentRow.cleanDescription || parentRow.rawDescription || parentRow.description || '',
            amount: parentRow.amount,
            currency: parentRow.currency || '£',
            date: parentRow.transactionDate || parentRow.transaction_date || parentRow.date,
            childIds,
          },
        }));
      }
      await getStagingItems(importId, { silent: true });
      if (retryMatch) await retryMatch(importId);
    }
    setProcessing((p) => ({ ...p, [rowId]: false }));
    setSplitModal(null);
    setSplitInlineRow(null);
    setSplitLines([]);
  }, [splitModal, splitInlineRow, splitLines, importId, splitStagingRow, retryMatch, getStagingItems, stagingItems]);

  const toggleCompactRowExpand = useCallback((rowId) => {
    const sid = String(rowId);
    setCompactExpandedKey((k) => (k === sid ? null : sid));
  }, []);

  const splitTotal = useMemo(() => splitLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0), [splitLines]);

  const handleAiSuggest = useCallback(async (text, includeClarify = false) => {
    if (!text || text.length < 2 || !suggestFactorFromText) return;
    setAiSuggestLoading(true);
    setAlternatesOpen(false);
    if (!includeClarify) {
      setAiSuggestResponse(null);
      setClarifyReplies({});
      setLocationOverride(null);
    }
    const rowDate = searchModal?.transactionDate || searchModal?.date;
    const dateEnd = rowDate || new Date().toISOString().slice(0, 10);
    const rowSource = searchModal?.rowSourceKind || searchModal?.sourceKind || currentJob?.effectiveSourceKind;
    const isSpend = rowSource === 'BANK' || rowSource === 'LEDGER' || searchModal?.dataKind === 'SPEND';
    const currency = searchModal?.currency || searchModal?.detectedCurrency || currentJob?.currency;

    const payload = {
      text: isSpend ? `${text} (${currency || 'GBP'} spend)` : text,
      date_end: dateEnd,
      org_country: orgCountry,
      include_average_fallback: true,
    };

    if (currency) payload.price = currency;
    if (locationOverride) payload.finallocation = locationOverride;

    if (includeClarify) {
      const cr = Object.fromEntries(Object.entries(clarifyReplies).filter(([, v]) => v != null && String(v).trim() !== ''));
      if (Object.keys(cr).length > 0) payload.clarify_replies = cr;
      if (aiSuggestResponse?.clarify_round != null) {
        const rn = Number(aiSuggestResponse.clarify_round);
        if (!Number.isNaN(rn)) payload.clarify_round = rn;
      }
    }

    try {
      const res = await suggestFactorFromText(payload);
      if (res?.data) {
        setAiSuggestResponse(res.data);
        setAlternatesOpen(Boolean(res.data.needs_review));
        const derivedAmt = res.data.usage?.amount;
        if (derivedAmt != null && derivedAmt !== '' && !searchModalActivity) {
          setSearchModalActivity(String(derivedAmt));
        }
      } else {
        setAiSuggestResponse(null);
      }
    } catch {
      setAiSuggestResponse(null);
    }
    setAiSuggestLoading(false);
  }, [suggestFactorFromText, clarifyReplies, searchModal, orgCountry, aiSuggestResponse, currentJob, locationOverride, searchModalActivity]);

  const handleLocationChange = useCallback(async (locationId) => {
    setLocationOverride(locationId);
    if (!searchQuery || searchQuery.length < 2 || !suggestFactorFromText) return;
    setAiSuggestLoading(true);
    setAlternatesOpen(false);

    const rowDate = searchModal?.transactionDate || searchModal?.date;
    const dateEnd = rowDate || new Date().toISOString().slice(0, 10);
    const rowSource = searchModal?.rowSourceKind || searchModal?.sourceKind || currentJob?.effectiveSourceKind;
    const isSpend = rowSource === 'BANK' || rowSource === 'LEDGER' || searchModal?.dataKind === 'SPEND';
    const currency = searchModal?.currency || searchModal?.detectedCurrency || currentJob?.currency;

    const payload = {
      text: isSpend ? `${searchQuery} (${currency || 'GBP'} spend)` : searchQuery,
      date_end: dateEnd,
      org_country: orgCountry,
      include_average_fallback: true,
      finallocation: locationId,
    };
    if (currency) payload.price = currency;

    try {
      const res = await suggestFactorFromText(payload);
      if (res?.data) {
        setAiSuggestResponse(res.data);
        setAlternatesOpen(Boolean(res.data.needs_review));
        const derivedAmt = res.data.usage?.amount;
        if (derivedAmt != null && derivedAmt !== '' && !searchModalActivity) {
          setSearchModalActivity(String(derivedAmt));
        }
      }
    } catch {
      /* keep previous response */
    }
    setAiSuggestLoading(false);
  }, [searchQuery, suggestFactorFromText, searchModal, currentJob, orgCountry, searchModalActivity]);

  const handleSelectCandidate = useCallback(async (candidate) => {
    if (!searchModal) return;
    const rowId = searchModal.id || searchModal.stagingId;
    const matchLike = candidateToMatch(candidate, searchModal);
    const entities = matchLike.entities || [];
    const factorId = matchLike.factorId || matchLike.factor_id;
    const factorName = matchLike.factorName || matchLike.factor_name;

    const resolveSuggestActivity = () => {
      const typed = (searchModalActivity || '').trim();
      if (typed) return typed;
      const fromUsage = aiSuggestResponse?.usage?.amount;
      if (fromUsage != null && fromUsage !== '') return String(fromUsage);
      const eff = getEffectiveActivityDetectedAmount(matchLike, searchModal, metricsById);
      if (eff != null && eff !== '') return String(eff);
      return '';
    };

    if (!isSpendFactor(matchLike, metricsById)) {
      const amt = resolveSuggestActivity();
      if (!amt || Number.isNaN(parseFloat(amt))) {
        setSearchModalActivityError('Enter an activity amount (quantity) for this factor before confirming.');
        return;
      }
    }
    setSearchModalActivityError('');

    const activityOverride = (searchModalActivity || '').trim() || undefined;

    if (entities.length > 1) {
      setEntityPickerRow({
        row: searchModal,
        match: matchLike,
        fromAiSuggest: true,
        candidate,
      });
      setSelectedEntities((prev) => ({ ...prev, [rowId]: prev[rowId] || entities[0]?.id }));
      if (activityOverride) {
        setActivityAmounts((prev) => ({ ...prev, [rowId]: activityOverride }));
      }
      return;
    }

    const entityId = entities[0]?.id;
    const ok = await handleConfirm(searchModal, matchLike, rememberChoice, entityId, activityOverride);
    if (ok !== true) return;

    if (factorId && searchQuery?.trim() && submitFactorSuggestFeedback) {
      const cr = Object.fromEntries(Object.entries(clarifyReplies).filter(([, v]) => v != null && String(v).trim() !== ''));
      const alternatesShown = aiSuggestResponse?.alternates?.map((a) => a.factor_id).filter(Boolean);
      submitFactorSuggestFeedback({
        text: searchQuery.trim(),
        factor_id: factorId,
        factor_name: factorName,
        org_country: orgCountry,
        clarify_replies: Object.keys(cr).length > 0 ? cr : undefined,
        remember_choice: rememberChoice,
        alternates_shown: alternatesShown?.length > 0 ? alternatesShown : undefined,
        confidence_score: candidate.signals?.llm_confidence ?? candidate.signals?.formula_score ?? undefined,
        clarify_rounds: aiSuggestResponse?.clarify_round ?? undefined,
      }).catch(() => {});
    }

    setSearchModal(null);
    setSearchQuery('');
    setSearchModalActivity('');
    setAiSuggestResponse(null);
    setClarifyReplies({});
    setAlternatesOpen(false);
    setLocationOverride(null);
  }, [searchModal, searchModalActivity, handleConfirm, searchQuery, clarifyReplies, aiSuggestResponse, orgCountry, rememberChoice, submitFactorSuggestFeedback, metricsById]);

  if (loading?.matches) {
    return <div className='text-center py-12 text-muted-foreground'>Loading factor matches...</div>;
  }

  const effectiveJob = job || currentJob;
  const importFilename = effectiveJob?.filename || effectiveJob?.fileName || effectiveJob?.originalFilename || null;
  const showDedupTechnicalIds = Boolean(cyfAdmin || whiteLabelAdmin);

  const jobStatus = job?.pipelineStatus || currentJob?.pipelineStatus;

  /** Full reconciliation card for a top-level staging row.
   *  `embedded: true`  — border merges with compact row above.
   *  `compactLayout: true` — hides source column; proposed-usage content aligns with compact grid. */
  const topLevelRowDetail = (row, { embedded = false, compactLayout = false } = {}) => {
    const key = row.id || row.stagingId;
    const matches = matchMap[key] || [];
    const topMatch = matches[0];
    const rowStatus = row.pipelineStatus || row.status;
    const isConfirmed = rowStatus === 'CONFIRMED';
    const isExcluded = rowStatus === 'EXCLUDED';
    const isCommitError = rowStatus === 'COMMIT_ERROR';
    const isCommitted = rowStatus === 'COMMITTED';
    const isSplit = rowStatus === 'SPLIT';
    const isDuplicateSkipped = rowStatus === STAGING_ROW_STATUSES.DUPLICATE_SKIPPED;
    const isReviewable = !isConfirmed && !isExcluded && !isCommitError && !isCommitted && !isSplit && !isDuplicateSkipped;
    const txnDate = row.transactionDate || row.transaction_date || row.date;
    const qualityTier = topMatch?.qualityTier || topMatch?.quality_tier;
    const upgradeAvailable = topMatch?.qualityUpgradeAvailable || topMatch?.quality_upgrade_available;
    const upgradeNote = topMatch?.qualityUpgradeNote || topMatch?.quality_upgrade_note;
    const activityUpgrade = parseActivityUpgrade(topMatch);
    const spendFallback = parseSpendFallback(topMatch);
    const premiumUpgrade = parsePremiumUpgrade(topMatch);
    const currencyMismatch =
      topMatch?.currencyMismatch === true ||
      topMatch?.currency_mismatch === true;
    const fxProvisional =
      topMatch?.fxProvisional === true ||
      topMatch?.fx_provisional === true;
    const fxImperfect =
      topMatch?.fxImperfect === true ||
      topMatch?.fx_imperfect === true ||
      currencyMismatch;
    const rowCurrencyLabel = String(
      topMatch?.rowCurrency || topMatch?.row_currency || row.currency || ''
    ).toUpperCase();
    const factorSpendCurrencyLabel = String(
      topMatch?.factorSpendCurrency || topMatch?.factor_spend_currency || ''
    ).toUpperCase();
    const needsActivityAmount =
      row.needsActivityAmount ||
      row.needs_activity_amount ||
      topMatch?.needsActivityAmount ||
      topMatch?.needs_activity_amount;
    const detectedUnit = pickFirst(row, 'detectedUnit', 'detected_unit') || pickFirst(topMatch, 'detectedUnit', 'detected_unit');
    const hasExplicitActivityAmount = getEffectiveActivityDetectedAmount(topMatch, row, metricsById) != null;
    const matchedFacilityName = pickFirst(row, 'matchedFacility', 'matched_facility') || pickFirst(topMatch, 'matchedFacility', 'matched_facility');
    const detPeriodStart = pickFirst(row, 'detectedPeriodStart', 'detected_period_start') || pickFirst(topMatch, 'detectedPeriodStart', 'detected_period_start');
    const detPeriodEnd = pickFirst(row, 'detectedPeriodEnd', 'detected_period_end') || pickFirst(topMatch, 'detectedPeriodEnd', 'detected_period_end');
    const detPeriodLabel = pickFirst(row, 'detectedPeriodLabel', 'detected_period_label') || pickFirst(topMatch, 'detectedPeriodLabel', 'detected_period_label');
    const inferredBounds = inferPeriodBoundsFromLabel(detPeriodLabel);
    const effectiveDetPeriodStart =
      detPeriodStart || inferredBounds.start || normaliseIsoDateish(txnDate);
    const effectiveDetPeriodEnd = detPeriodEnd || inferredBounds.end || effectiveDetPeriodStart;
    const resolvedFacilityId = resolveStagingFacilityId(key, matchedFacilityName, facilityOverrides, orgFacilities);
    const entities = topMatch?.entities || [];
    const chosenEntityId = selectedEntities[key] && entities.some((e) => String(e.id) === String(selectedEntities[key]))
      ? selectedEntities[key]
      : entities[0]?.id;
    const chosenEntity = entities.find((e) => String(e.id) === String(chosenEntityId)) || entities[0];

    if (isSplit) return null;

    const openSearchForRow = () => {
      const desc = row.cleanDescription || row.rawDescription || '';
      setSearchModal(row);
      setSearchQuery(desc);
      setAiSuggestResponse(null);
      setClarifyReplies({});
      setAlternatesOpen(false);
      setLocationOverride(null);
      if (desc.length >= 2) handleAiSuggest(desc);
    };

    const dedupCtx = getDedupContext(row);
    const dedupScope = getDedupScope(row);
    const linkedDedupId =
      row.linkedDedupStagingId ||
      row.linked_dedup_staging_id ||
      dedupCtx?.linked_staging_id ||
      dedupCtx?.linkedStagingId;
    const linkedDedupRow = linkedDedupId
      ? stagingItems.find((s) => String(s.id || s.stagingId) === String(linkedDedupId))
      : null;
    const linkedDescFromCtx = pickFirst(dedupCtx, 'linked_description', 'linkedDescription');
    const linkedFilenameFromCtx = pickFirst(dedupCtx, 'linked_filename', 'linkedFilename');
    const linkedDateFromCtx = pickFirst(dedupCtx, 'linked_date', 'linkedDate');
    const linkedImportIdFromCtx = pickFirst(dedupCtx, 'linked_import_id', 'linkedImportId');
    const isCrossImportDedup =
      dedupScope === 'SAME_IMPORT'
        ? false
        : dedupScope === 'CROSS_IMPORT' ||
          linkedImportIdFromCtx ||
          row.linkedImportJobId ||
          row.linked_import_job_id;
    const displayLinkedDesc =
      linkedDescFromCtx ||
      linkedDedupRow?.rawDescription ||
      linkedDedupRow?.cleanDescription ||
      '';
    const displayLinkedDate =
      linkedDateFromCtx ||
      linkedDedupRow?.transactionDate ||
      linkedDedupRow?.transaction_date ||
      linkedDedupRow?.date ||
      '';
    const displayLinkedFilename = linkedFilenameFromCtx || '';
    const duplicateSkippedMessage = isCrossImportDedup
      ? `This row was already imported in ${displayLinkedFilename || 'another import'}. The original transaction ('${displayLinkedDesc || '—'}', ${displayLinkedDate || '—'}) has already been processed.`
      : displayLinkedDesc
        ? `This row is a duplicate of another row in this file ('${displayLinkedDesc}').`
        : 'This row is a duplicate of another row in this file.';
    const rowProcKey = row.id || row.stagingId;
    const flashMatchedDetail = matchSuccessFlash[String(key)];

    const renderProposedUsage = () => (
      isDuplicateSkipped ? (
              <div className='space-y-2'>
                <Badge className='bg-muted text-muted-foreground border-border'>Duplicate skipped</Badge>
                <p className='text-sm text-foreground'>{duplicateSkippedMessage}</p>
                {importFilename && (
                  <p className='text-xs text-muted-foreground'>
                    <span className='text-muted-foreground'>This file: </span>
                    <span className='font-medium text-foreground'>{importFilename}</span>
                  </p>
                )}
                {linkedDedupRow && (
                  <div className='rounded-md border border-border bg-muted/30 px-3 py-2 text-xs'>
                    <p className='text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1'>
                      {isCrossImportDedup ? 'Kept transaction (in staging list)' : 'Kept row in this import'}
                    </p>
                    <p className='font-medium text-foreground'>{linkedDedupRow.cleanDescription || linkedDedupRow.rawDescription || '—'}</p>
                    <p className='text-muted-foreground font-mono mt-0.5'>
                      {linkedDedupRow.currency || '£'}
                      {Number(linkedDedupRow.amount || 0).toFixed(2)}
                      {(linkedDedupRow.transactionDate || linkedDedupRow.transaction_date || linkedDedupRow.date) && (
                        <span className='ml-2'>{linkedDedupRow.transactionDate || linkedDedupRow.transaction_date || linkedDedupRow.date}</span>
                      )}
                    </p>
                  </div>
                )}
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  loading={Boolean(processing[rowProcKey])}
                  disabled={Boolean(processing[rowProcKey])}
                  onClick={() => handleDedupOverride(row)}
                >
                  Not a duplicate — include this row
                </Button>
                {showDedupTechnicalIds &&
                  (linkedDedupId ||
                    row.dedupKey ||
                    dedupCtx ||
                    row.dedupScope ||
                    row.dedup_scope ||
                    row.linkedImportJobId ||
                    row.linked_import_job_id) && (
                  <details className='text-[10px] text-muted-foreground mt-1 group'>
                    <summary className='cursor-pointer text-primary hover:underline list-none [&::-webkit-details-marker]:hidden'>
                      Technical details (admin)
                    </summary>
                    <div className='mt-2 space-y-1.5 pl-0 pt-1 border-t border-border font-mono break-all text-[10px]'>
                      {dedupCtx && (
                        <pre className='whitespace-pre-wrap text-[10px] leading-relaxed'>{JSON.stringify(dedupCtx, null, 2)}</pre>
                      )}
                      {(row.dedupScope || row.dedup_scope) && (
                        <p>
                          <span className='text-muted-foreground'>dedupScope: </span>
                          {String(row.dedupScope || row.dedup_scope)}
                        </p>
                      )}
                      {(row.linkedImportJobId || row.linked_import_job_id) && (
                        <p>
                          <span className='text-muted-foreground'>linkedImportJobId: </span>
                          {String(row.linkedImportJobId || row.linked_import_job_id)}
                        </p>
                      )}
                      {linkedDedupId && (
                        <p>
                          <span className='text-muted-foreground'>linkedDedupStagingId: </span>
                          {linkedDedupId}
                        </p>
                      )}
                      {row.dedupKey && (
                        <p>
                          <span className='text-muted-foreground'>dedupKey: </span>
                          {String(row.dedupKey)}
                        </p>
                      )}
                    </div>
                  </details>
                )}
              </div>
            ) : isExcluded ? (
              <p className='text-sm text-muted-foreground italic'>Excluded</p>
            ) : isCommitError ? (
              <div className='space-y-1'>
                <p className='text-sm font-medium text-foreground whitespace-normal break-words leading-snug'>{topMatch?.factorName || 'Unknown factor'}</p>
                <p className='text-xs text-danger'>{row.commitError || row.lastError || 'Commit failed'}</p>
              </div>
            ) : topMatch ? (
              <>
                <div className='mb-1'>
                  <p className='text-[10px] text-muted-foreground uppercase tracking-wider'>Matched Factor</p>
                  <p className='text-sm font-medium text-foreground whitespace-normal break-words leading-snug'>{topMatch.factorName}</p>
                  <div className='text-xs text-muted-foreground'>
                    {topMatch.co2ePerUnit ? `${fmt2dp(topMatch.co2ePerUnit)} kgCO₂e/${getMetricLabel(topMatch, metricsById)}` : ''}
                    {topMatch.subentity && !/^\d+$/.test(topMatch.subentity) ? ` · ${topMatch.subentity}` : ''}
                  </div>
                </div>
                {(() => {
                  const topIsSpend = isSpendFactor(topMatch, metricsById);
                  const effectiveAct = getEffectiveActivityDetectedAmount(topMatch, row, metricsById);
                  const detectedAmt = effectiveAct;
                  const actAmt =
                    activityAmounts[key] !== undefined
                      ? activityAmounts[key]
                      : effectiveAct != null
                        ? String(effectiveAct)
                        : '';
                  if (topIsSpend) {
                    const sp = resolveSpendForFactor(row, topMatch, metricsById);
                    const em = computeSpendEmissions(row, topMatch, metricsById);
                    if (em != null) {
                      return (
                        <div className='mb-1'>
                          <p className='text-[10px] text-muted-foreground uppercase tracking-wider'>Estimated Emissions</p>
                          <p className='text-sm font-medium text-foreground'>
                            {sp.amount != null
                              ? `${symbolForCurrencyCode(sp.currencyCode)}${sp.amount.toFixed(2)}`
                              : `${row.currency || '£'}${Number(row.amount || 0).toFixed(2)}`}
                            {' '}
                            → <strong>{em} kgCO₂e</strong>
                          </p>
                          {(sp.source === 'backend' || sp.source === 'match') && (
                            <p className='text-[10px] text-muted-foreground mt-0.5'>Spend uses the converted amount from import (aligned with this factor’s currency).</p>
                          )}
                        </div>
                      );
                    }
                    if (sp.source === 'missing') {
                      return (
                        <div className='mb-1 rounded-md border border-warning/30 bg-warning-bg/10 px-2 py-1.5'>
                          <p className='text-[11px] text-warning'>
                            This factor is priced in {sp.currencyCode}. The import row has no {sp.currencyCode} spend from conversion yet, so emissions cannot be estimated here. The server may still apply FX on confirm.
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }
                  const actEmissions = actAmt ? (parseFloat(actAmt) * (topMatch.co2ePerUnit || 0)).toFixed(1) : null;
                  const metricLbl = getMetricLabel(topMatch, metricsById);
                  return (
                    <div className='mb-1'>
                      <p className='text-[10px] text-muted-foreground uppercase tracking-wider'>Activity Amount</p>
                      <div className='flex items-center gap-2 mt-0.5 flex-wrap'>
                        <Input
                          className='w-20 h-7 text-xs bg-background px-1.5 font-mono'
                          type='number'
                          step='any'
                          placeholder={metricLbl}
                          value={actAmt}
                          onChange={(e) => setActivityAmounts((prev) => ({ ...prev, [key]: e.target.value }))}
                        />
                        <span className='text-xs text-muted-foreground'>{metricLbl}</span>
                        <span className='text-xs text-muted-foreground'>→</span>
                        <span className='text-xs font-semibold text-foreground'>
                          {actEmissions ? `${actEmissions} kgCO₂e` : '? kgCO₂e'}
                        </span>
                      </div>
                      {detectedAmt != null && detectedAmt !== '' && (
                        <p className='text-[10px] text-muted-foreground mt-0.5 italic'>
                          Pre-filled from description ({detectedAmt} {metricLbl})
                        </p>
                      )}
                      {formatOriginalFinancialContext(row) && (
                        <p className='text-[10px] text-muted-foreground mt-0.5'>{formatOriginalFinancialContext(row)}</p>
                      )}
                    </div>
                  );
                })()}
                <div className='flex flex-wrap gap-x-4 gap-y-1 items-center text-xs'>
                  {chosenEntity && (
                    <span>
                      <span className='text-muted-foreground'>Entity: </span>
                      <span className='font-medium text-foreground'>{chosenEntity.name}</span>
                      {entities.length > 1 && isReviewable && (
                        <button
                          type='button'
                          className='ml-1 text-primary hover:underline text-[11px]'
                          onClick={() => openEntityCategoryPicker(row, topMatch)}
                        >
                          (change)
                        </button>
                      )}
                    </span>
                  )}
                  {!isReviewable &&
                    (detPeriodLabel ? (
                      <span>
                        <span className='text-muted-foreground'>Period: </span>
                        <span className='font-mono font-medium text-foreground'>{detPeriodLabel}</span>
                      </span>
                    ) : (
                      <span>
                        <span className='text-muted-foreground'>Period: </span>
                        <span className='font-mono'>
                          {txnDate || <span className='italic text-muted-foreground'>TBC</span>}
                        </span>
                      </span>
                    ))}
                  {detectedUnit && (
                    <span>
                      <span className='text-muted-foreground'>Unit: </span>
                      <span className='font-medium text-foreground'>{detectedUnit}</span>
                    </span>
                  )}
                </div>
                {isReviewable && (matchedFacilityName || facilityOptions.length > 0) && (
                  <div className='flex flex-nowrap items-center gap-2 mt-1.5 text-xs text-muted-foreground w-full min-w-0'>
                    <span className='shrink-0 inline-flex items-center gap-1'>
                      <MapPin size={12} strokeWidth={1.5} className='text-muted-foreground' />
                      <span>Facility:</span>
                    </span>
                    {facilityOptions.length > 0 ? (
                      <div className='flex-1 min-w-0 max-w-[min(100%,19rem)]'>
                        <NewSelect
                          searchable
                          clearable
                          size='xs'
                          placeholder='Select facility'
                          searchPlaceholder='Search facilities...'
                          maxHeight='160px'
                          dropdownMenuClassName='z-[100] min-w-full w-max max-w-[min(calc(100vw-1.5rem),22rem)]'
                          className='w-full'
                          value={resolvedFacilityId || ''}
                          options={facilityOptions}
                          onChange={(val) =>
                            setFacilityOverrides((prev) => {
                              const next = { ...prev };
                              const autoId = resolveStagingFacilityId(key, matchedFacilityName, {}, orgFacilities);
                              if (val == null || val === '' || String(val) === String(autoId)) delete next[key];
                              else next[key] = String(val);
                              return next;
                            })
                          }
                        />
                      </div>
                    ) : matchedFacilityName ? (
                      <span className='font-medium text-foreground truncate'>{matchedFacilityName}</span>
                    ) : null}
                  </div>
                )}
                {!isReviewable && matchedFacilityName && (
                  <div className='flex items-center gap-1.5 mt-1.5 text-[11px]'>
                    <MapPin size={11} strokeWidth={1.5} className='text-muted-foreground' />
                    <span className='text-muted-foreground'>Facility:</span>
                    <span className='font-medium text-foreground'>{matchedFacilityName}</span>
                  </div>
                )}
                {isReviewable && (
                  <div className='flex flex-wrap items-center gap-2 mt-1.5 text-[11px]'>
                    <span className='text-muted-foreground shrink-0'>Date range:</span>
                    <Input
                      type='date'
                      className='w-[130px] h-6 text-[11px] bg-background px-1.5 font-mono'
                      value={periodOverrides[key]?.start || effectiveDetPeriodStart || ''}
                      onChange={(e) => setPeriodOverrides((prev) => ({
                        ...prev,
                        [key]: { ...prev[key], start: e.target.value },
                      }))}
                    />
                    <span className='text-muted-foreground'>to</span>
                    <Input
                      type='date'
                      className='w-[130px] h-6 text-[11px] bg-background px-1.5 font-mono'
                      value={periodOverrides[key]?.end || effectiveDetPeriodEnd || ''}
                      onChange={(e) => setPeriodOverrides((prev) => ({
                        ...prev,
                        [key]: { ...prev[key], end: e.target.value },
                      }))}
                    />
                  </div>
                )}
                <div className='flex items-center gap-3 mt-2'>
                  <QualityBadge level={qualityBadgeLevelForMatch(topMatch, row)} />
                  <ConfidenceBar confidence={topMatch.confidence} />
                </div>

                {/* Activity upgrade — backend now sends a structured activity_upgrade when a Silver alternative exists */}
                {activityUpgrade && isReviewable && (
                  <div className='flex items-center gap-2 mt-2 py-1.5 px-2.5 rounded-md bg-amber-50 border border-amber-200/30 text-[11px] text-amber-800 flex-wrap'>
                    <Sparkles size={12} className='text-amber-600 shrink-0' strokeWidth={1.5} />
                    <span className='font-semibold'>↑ {activityUpgrade.qualityTier || activityUpgrade.quality_tier || 'Silver'}:</span>
                    <span className='text-muted-foreground truncate'>{(() => { const n = activityUpgrade.factorName || activityUpgrade.factor_name || ''; const idx = n.lastIndexOf(' · '); return idx > 0 ? n.slice(0, idx) : (n || 'Enter activity amount for better accuracy'); })()}</span>
                    <Input
                      className='w-16 h-6 text-[11px] bg-background px-1.5'
                      placeholder='qty'
                      value={upgradeAmounts[key] || ''}
                      onChange={(e) => setUpgradeAmounts((prev) => ({ ...prev, [key]: e.target.value }))}
                    />
                    <span className='text-muted-foreground'>{resolveUpgradeUnitLabel(activityUpgrade, topMatch, metricsById)}</span>
                    <Button
                      variant='amber'
                      size='sm'
                      className='text-[10px] h-6 px-2'
                      disabled={!upgradeAmounts[key]}
                      onClick={() => {
                        const upgMatch = {
                          ...topMatch,
                          factorId: activityUpgrade.factorId || activityUpgrade.factor_id,
                          factorName: activityUpgrade.factorName || activityUpgrade.factor_name,
                          qualityTier: activityUpgrade.qualityTier || activityUpgrade.quality_tier || 'silver',
                        };
                        handleConfirm(row, upgMatch, rememberChoice, undefined, upgradeAmounts[key]);
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                )}

                {/* Spend fallback — when top is activity and user prefers the money total */}
                {spendFallback && !hasExplicitActivityAmount && isReviewable && (
                  <div className='flex items-center gap-2 mt-2 py-1.5 px-2.5 rounded-md bg-slate-50 border border-slate-200/60 text-[11px] text-slate-700 flex-wrap'>
                    <span className='font-semibold'>Use spend instead:</span>
                    <span className='text-muted-foreground truncate'>
                      {(() => {
                        const n = spendFallback.factorName || spendFallback.factor_name || '';
                        const idx = n.lastIndexOf(' · ');
                        return idx > 0 ? n.slice(0, idx) : n;
                      })()}
                    </span>
                    <span className='text-muted-foreground font-mono'>
                      {spendFallback.amount != null
                        ? `${spendFallback.currency ? String(spendFallback.currency).toUpperCase() : ''} ${spendFallback.amount}`.trim()
                        : (row.amount != null ? String(row.amount) : '')}
                    </span>
                    <Button
                      variant='outline'
                      size='sm'
                      className='text-[10px] h-6 px-2'
                      onClick={() => {
                        const spendMatch = {
                          ...topMatch,
                          factorId: spendFallback.factorId || spendFallback.factor_id,
                          factorName: spendFallback.factorName || spendFallback.factor_name,
                          qualityTier: spendFallback.qualityTier || spendFallback.quality_tier || 'bronze',
                          unitFamily: 'spend',
                          unit_family: 'spend',
                        };
                        handleConfirm(row, spendMatch, rememberChoice);
                      }}
                    >
                      Use spend
                    </Button>
                  </div>
                )}

                {/* Cross-currency spend FX — imperfect / provisional */}
                {currencyMismatch && isReviewable && (
                  <div className='mt-2 py-1.5 px-2.5 rounded-md bg-orange-50 border border-orange-200/50 text-[11px] text-orange-900 space-y-0.5'>
                    <div>
                      <span className='font-semibold'>Currency mismatch: </span>
                      Factor is {factorSpendCurrencyLabel || 'another currency'} spend
                      {rowCurrencyLabel ? `; row is ${rowCurrencyLabel}` : ''}.
                      {' '}Spend intensities differ by currency — amount will be FX’d at commit.
                    </div>
                    {fxProvisional && (
                      <div className='text-orange-800/90'>
                        Year-end rate not available yet; commit will use a provisional daily rate (differs from final EOY).
                      </div>
                    )}
                    {!fxProvisional && fxImperfect && (
                      <div className='text-orange-800/90'>
                        FX is a degraded approximation versus a same-currency spend factor.
                      </div>
                    )}
                  </div>
                )}
                {!currencyMismatch && fxProvisional && isReviewable && (
                  <div className='mt-2 py-1.5 px-2.5 rounded-md bg-orange-50 border border-orange-200/50 text-[11px] text-orange-900'>
                    Year-end FX rate not available yet; a provisional daily rate will be used at commit.
                  </div>
                )}

                {/* Legacy upgrade fallback — keep for rows with old qualityUpgradeAvailable flag */}
                {!activityUpgrade && upgradeAvailable && qualityTier === 'bronze' && isReviewable && (
                  <div className='flex items-center gap-2 mt-2 py-1.5 px-2.5 rounded-md bg-amber-50 border border-amber-200/30 text-[11px] text-amber-800 flex-wrap'>
                    <Sparkles size={12} className='text-amber-600 shrink-0' strokeWidth={1.5} />
                    <span className='font-semibold'>↑ Silver:</span>
                    <span className='text-muted-foreground truncate'>{upgradeNote || 'Enter activity amount for better accuracy'}</span>
                    <Input
                      className='w-16 h-6 text-[11px] bg-background px-1.5'
                      placeholder='qty'
                      value={upgradeAmounts[key] || ''}
                      onChange={(e) => setUpgradeAmounts((prev) => ({ ...prev, [key]: e.target.value }))}
                    />
                    <span className='text-muted-foreground'>{getMetricLabel(topMatch, metricsById)} →</span>
                    <span className='font-semibold text-info'>
                      {upgradeAmounts[key] ? `${(parseFloat(upgradeAmounts[key]) * (topMatch?.co2ePerUnit || 0)).toFixed(1)} kgCO₂e` : '? kgCO₂e'}
                    </span>
                    <Button
                      variant='amber'
                      size='sm'
                      className='text-[10px] h-6 px-2'
                      disabled={!upgradeAmounts[key]}
                      onClick={() => {
                        handleConfirm(row, topMatch);
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                )}

                {premiumUpgrade && isReviewable && (
                  <div className='flex items-center gap-2 mt-2 py-1.5 px-2.5 rounded-md bg-primary/5 border border-primary/20 text-[11px] text-foreground flex-wrap'>
                    <Lock size={12} className='text-primary shrink-0' strokeWidth={1.5} />
                    <span className='flex-1 min-w-0'>
                      <span className='font-semibold'>{premiumUpgrade.factor_name || 'Better match available'}</span>
                      {premiumUpgrade.quality_tier && <span className='text-muted-foreground'> ({premiumUpgrade.quality_tier})</span>}
                      <span className='text-muted-foreground'> — </span>
                      <span className='font-medium text-primary'>{premiumUpgrade.add_on_name || 'premium dataset'}</span>
                    </span>
                    <Button
                      variant='outline'
                      size='sm'
                      className='text-[10px] h-6 px-2 border-primary/30 text-primary hover:bg-primary/10'
                      onClick={() => openUpgradeDialog(premiumUpgrade.required_add_on_id, premiumUpgrade.add_on_name)}
                    >
                      Upgrade
                    </Button>
                  </div>
                )}

                {/* Needs activity amount — inline prompt */}
                {needsActivityAmount && !upgradeAvailable && !activityUpgrade && isReviewable && (
                  <div className='flex items-center gap-2 mt-2 py-1.5 px-2.5 rounded-md bg-info-bg border border-info/20 text-[11px] flex-wrap'>
                    <span className='text-info font-semibold shrink-0'>Activity amount needed:</span>
                    <Input
                      className='w-16 h-6 text-[11px] bg-background px-1.5'
                      placeholder='e.g. 12.5'
                      value={activityAmounts[key] || ''}
                      onChange={(e) => setActivityAmounts((prev) => ({ ...prev, [key]: e.target.value }))}
                    />
                    <span className='text-muted-foreground'>{getMetricLabel(topMatch, metricsById)}</span>
                    <Button size='sm' className='text-[10px] h-6 px-2' disabled={!activityAmounts[key]}>Save</Button>
                  </div>
                )}
              </>
            ) : rematchingRows[String(key)] ? (
              <div className='flex items-center gap-2'>
                <div className='h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-muted border-t-primary' />
                <p className='text-sm text-muted-foreground'>Finding a factor for this row…</p>
              </div>
            ) : rejectedFactors[key]?.length > 0 ? (
              <p className='text-sm text-warning italic'>All suggestions rejected</p>
            ) : (
              <p className='text-sm text-muted-foreground italic'>No conversion factor matched this transaction</p>
            )
    );

    const renderRowActions = () => (
      <>
          {isReviewable && (
            <>
              <div className='flex gap-1.5 flex-wrap'>
                {topMatch && (() => {
                  const topNonSpend = !isSpendFactor(topMatch, metricsById);
                  const topEff = getEffectiveActivityDetectedAmount(topMatch, row, metricsById);
                  const topResolvedAct =
                    (activityAmounts[key] !== undefined && activityAmounts[key] !== ''
                      ? activityAmounts[key]
                      : topEff != null
                        ? String(topEff)
                        : '').trim();
                  const topNeedsAmt = topNonSpend && !topResolvedAct;
                  return (
                    <>
                      <Button
                        variant='default'
                        size='sm'
                        onClick={() => handleConfirm(row, topMatch)}
                        disabled={processing[key] || topNeedsAmt}
                        className='text-xs'
                        title={topNeedsAmt ? `Enter ${getMetricLabel(topMatch, metricsById)} amount first` : ''}
                      >
                        <Check size={13} strokeWidth={2} />Confirm
                      </Button>
                      <Button variant='outline' size='sm' onClick={() => handleReject(row, topMatch)} disabled={processing[key]} className='text-xs text-danger border-danger/30 hover:bg-danger/5'>
                        <X size={13} strokeWidth={2} />Reject
                      </Button>
                    </>
                  );
                })()}
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => {
                    if (splitInlineRow === key) {
                      const hasData = splitLines.some((l) => l.description.trim() || l.amount);
                      if (hasData) {
                        if (!window.confirm('Discard split lines? Any entered data will be lost.')) return;
                      }
                      setSplitInlineRow(null);
                      setSplitLines([]);
                    } else {
                      setSplitInlineRow(key);
                      setSplitLines([
                        { description: '', amount: '', date: '' },
                        { description: '', amount: '', date: '' },
                      ]);
                    }
                  }}
                  disabled={processing[key]}
                  className='text-xs'
                >
                  <Scissors size={13} strokeWidth={1.5} />{splitInlineRow === key ? 'Cancel Split' : 'Split'}
                </Button>
                <Button variant='ghost' size='sm' onClick={openSearchForRow} disabled={processing[key]} className='text-xs'>
                  <Search size={13} strokeWidth={1.5} />Find factor
                </Button>
                {matches.length > 1 && (
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => setAlternatesOpenRows((prev) => ({ ...prev, [key]: !prev[key] }))}
                    className='text-xs'
                  >
                    {alternatesOpenRows[key] ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    Alternatives ({matches.length - 1})
                  </Button>
                )}
              </div>
              <Button
                variant='ghost'
                size='sm'
                className='text-xs text-muted-foreground'
                onClick={() => { setExcludeModal(row); setExcludeReason(EXCLUDE_REASONS[0].value); setExcludeNotes(''); }}
                disabled={processing[key]}
              >
                <Ban size={13} strokeWidth={1.5} />Exclude
              </Button>
            </>
          )}
          {isConfirmed && (
            <div className='flex items-center gap-2 flex-wrap'>
              <Badge className='bg-success-bg text-success'>Confirmed</Badge>
              <Button variant='outline' size='sm' className='text-xs h-7' onClick={() => handleUnconfirm(row)} disabled={processing[key]}>
                <Undo2 size={12} strokeWidth={1.5} />Edit / unconfirm
              </Button>
              <Button variant='link' size='sm' className='text-xs p-0 h-auto' onClick={openSearchForRow} disabled={processing[key]}>
                Change factor
              </Button>
            </div>
          )}
          {isCommitted && <Badge className='bg-primary/10 text-primary'>Committed</Badge>}
          {isExcluded && <Badge className='bg-muted text-muted-foreground'>Excluded</Badge>}
          {isCommitError && (
            <div className='flex items-center gap-2'>
              <Badge className='bg-danger/10 text-danger'>Commit Error</Badge>
              <Button variant='outline' size='sm' className='text-xs' onClick={openSearchForRow} disabled={processing[key]}>
                <Pencil size={13} strokeWidth={1.5} />Change factor
              </Button>
            </div>
          )}
          {isDuplicateSkipped && <Badge className='bg-muted text-muted-foreground'>Duplicate skipped</Badge>}
      </>
    );

    const renderAlternatives = () => (
        !isDuplicateSkipped && alternatesOpenRows[key] && matches.length > 1 ? (
          <div className='border-t border-border bg-muted/20 px-4 py-3'>
            <div className='flex items-center justify-between mb-2'>
              <p className='text-[10px] font-bold uppercase tracking-wider text-muted-foreground'>Alternative Matches <span className='font-normal normal-case'>(from AI suggestions)</span></p>
              <p className='text-[10px] text-muted-foreground italic'>Need something else? Use "Find Factor" to search all factors.</p>
            </div>
            <div className='flex flex-col gap-2'>
              {matches.slice(1).map((alt, i) => {
                const altKey = `${key}-alt-${i}`;
                const altIsSpend = isSpendFactor(alt, metricsById);
                const altEmissions = altIsSpend ? computeSpendEmissions(row, alt, metricsById) : null;
                const altSpend = altIsSpend ? resolveSpendForFactor(row, alt, metricsById) : null;
                const altEff = getEffectiveActivityDetectedAmount(alt, row, metricsById);
                const altAmount =
                  altActivityAmounts[altKey] !== undefined
                    ? altActivityAmounts[altKey]
                    : altEff != null
                      ? String(altEff)
                      : '';
                const altCalcEmissions = !altIsSpend && altAmount ? (parseFloat(altAmount) * (alt.co2ePerUnit || 0)).toFixed(1) : null;
                const altConfirmQty = altAmount.trim();

                return (
                  <div key={altKey} className='rounded-md border border-border bg-card px-3 py-2'>
                    <div className='flex items-center gap-3'>
                      <div className='flex-1 min-w-0'>
                        <p className='text-sm whitespace-normal break-words leading-snug'>{alt.factorName}</p>
                        <p className='text-xs text-muted-foreground'>
                          {alt.co2ePerUnit ? `${fmt2dp(alt.co2ePerUnit)} kgCO₂e/${getMetricLabel(alt, metricsById)}` : ''}
                          {alt.subentity && !/^\d+$/.test(alt.subentity) ? ` · ${alt.subentity}` : ''}
                        </p>
                      </div>
                      <ConfidenceBar confidence={alt.confidence} />
                      <QualityBadge level={qualityBadgeLevelForMatch(alt, row)} />
                      {altIsSpend && (
                        <Button variant='outline' size='sm' className='text-xs shrink-0' onClick={() => handleConfirm(row, alt)}>
                          Use this
                        </Button>
                      )}
                    </div>
                    {altIsSpend && (
                      <div className='flex flex-col gap-1 mt-2 text-[11px] text-muted-foreground'>
                        <div className='flex items-center gap-2 flex-wrap'>
                          <Lock className='h-3.5 w-3.5 shrink-0 text-muted-foreground' strokeWidth={1.5} />
                          <span className='font-semibold text-foreground'>Spend (locked)</span>
                          <span className='rounded-md border border-border bg-muted/40 px-2 py-0.5 font-mono tabular-nums text-foreground'>
                            {altSpend?.amount != null
                              ? `${symbolForCurrencyCode(altSpend.currencyCode)}${altSpend.amount.toFixed(2)}`
                              : '—'}
                          </span>
                          <span>→</span>
                          <span className='font-semibold text-info'>{altEmissions != null ? `${altEmissions} kgCO₂e` : '? kgCO₂e'}</span>
                        </div>
                        {altSpend?.source === 'backend' || altSpend?.source === 'match' ? (
                          <p className='text-[10px] text-muted-foreground pl-6'>From import conversion (matches factor currency).</p>
                        ) : null}
                        {altSpend?.source === 'missing' ? (
                          <p className='text-[10px] text-warning pl-6'>
                            No {altSpend.currencyCode} spend on this row from import conversion — estimate unavailable until conversion is available.
                          </p>
                        ) : null}
                        {altSpend?.source === 'row' && altSpend?.amount != null ? (
                          <p className='text-[10px] text-muted-foreground pl-6'>From transaction line (same currency as factor).</p>
                        ) : null}
                      </div>
                    )}
                    {!altIsSpend && (
                      <div className='flex flex-col gap-1 mt-2 text-[11px] flex-wrap'>
                        <div className='flex items-center gap-2 flex-wrap'>
                          <span className='text-amber-700 font-semibold'>↑ Enter {getMetricLabel(alt, metricsById)}:</span>
                          <Input
                            className='w-16 h-6 text-[11px] bg-background px-1.5'
                            placeholder={getMetricLabel(alt, metricsById)}
                            value={altAmount}
                            onChange={(e) => setAltActivityAmounts((prev) => ({ ...prev, [altKey]: e.target.value }))}
                          />
                          <span className='text-muted-foreground'>→</span>
                          <span className='font-semibold text-info'>
                            {altCalcEmissions ? `${altCalcEmissions} kgCO₂e` : '? kgCO₂e'}
                          </span>
                          <Button
                            variant='amber'
                            size='sm'
                            className='text-[10px] h-6 px-2'
                            disabled={!altConfirmQty}
                            onClick={() => handleConfirm(row, alt, rememberChoice, undefined, altConfirmQty)}
                            title={!altConfirmQty ? `Enter ${getMetricLabel(alt, metricsById)} first` : ''}
                          >
                            Use this
                          </Button>
                        </div>
                        {formatOriginalFinancialContext(row) && (
                          <p className='text-[10px] text-muted-foreground'>{formatOriginalFinancialContext(row)}</p>
                        )}
                        {altEff != null && altActivityAmounts[altKey] === undefined ? (
                          <p className='text-[10px] text-muted-foreground italic'>Pre-filled from description ({altEff} {getMetricLabel(alt, metricsById)})</p>
                        ) : null}
                        {alt.extractedAmount ? (
                          <span className='text-[10px] text-muted-foreground italic'>(pre-filled from receipt)</span>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null
    );

    const renderSplitEditor = () => (
        splitInlineRow === key ? (
          <div className='border-t-2 border-dashed border-amber-400 px-4 py-3 bg-amber-50/30'>
            <p className='text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-3'>
              <Scissors size={12} className='inline mr-1' strokeWidth={1.5} />
              Split {row.currency || '£'}{Number(row.amount || 0).toFixed(2)} into line items
            </p>

            <div className='flex flex-col gap-2'>
              {splitLines.map((line, i) => (
                <div key={i} className='grid grid-cols-1 md:grid-cols-2 border border-dashed border-amber-300/40 rounded-lg overflow-hidden'>
                  {/* Sub-row source */}
                  <div className='p-3 bg-card'>
                    <p className='text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5'>Source</p>
                    <Input
                      className='text-xs bg-background mb-1.5 h-8'
                      placeholder='Description (e.g. A4 paper)'
                      value={line.description}
                      onChange={(e) => setSplitLines((prev) => prev.map((l, j) => j === i ? { ...l, description: e.target.value } : l))}
                    />
                    <div className='flex gap-2 items-center'>
                      <Input
                        className='text-xs bg-background text-right font-mono w-24 h-8'
                        placeholder='Amount'
                        type='number'
                        step='0.01'
                        value={line.amount}
                        onChange={(e) => setSplitLines((prev) => prev.map((l, j) => j === i ? { ...l, amount: e.target.value } : l))}
                      />
                      <span className='text-xs text-muted-foreground'>{row.currency || '£'} spend</span>
                    </div>
                  </div>
                  {/* Sub-row usage — will be populated after split+match */}
                  <div className='p-3 bg-amber-50/20 border-l border-dashed border-amber-300/40'>
                    <p className='text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5'>Usage Record</p>
                    {line.description.trim() ? (
                      <p className='text-xs text-muted-foreground italic'>Factor will be matched after split is confirmed</p>
                    ) : (
                      <p className='text-xs text-muted-foreground italic'>Enter a description to match</p>
                    )}
                    {splitLines.length > 2 && (
                      <Button
                        variant='ghost'
                        size='sm'
                        className='text-xs text-danger mt-1 p-0 h-auto'
                        onClick={() => setSplitLines((prev) => prev.filter((_, j) => j !== i))}
                      >
                        <Trash2 size={11} strokeWidth={1.5} className='mr-1' />Remove
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <Button variant='outline' size='sm' className='text-xs mt-2' onClick={() => setSplitLines((prev) => [...prev, { description: '', amount: '', date: '' }])}>
              <Plus size={12} strokeWidth={1.5} />Add line item
            </Button>

            <div className='flex items-center justify-between mt-3 pt-2 border-t border-border text-sm font-semibold'>
              <span>Total: {row.currency || '£'}{splitTotal.toFixed(2)} of {row.currency || '£'}{Number(row.amount || 0).toFixed(2)}</span>
              {Math.abs(splitTotal - Number(row.amount || 0)) < 0.02 ? (
                <span className='text-xs text-success'>✓ Balanced</span>
              ) : (
                <span className='text-xs text-danger'>
                  {splitTotal > Number(row.amount || 0) ? 'Over' : 'Under'} by {row.currency || '£'}{Math.abs(splitTotal - Number(row.amount || 0)).toFixed(2)}
                </span>
              )}
            </div>

            <div className='flex gap-2 mt-3'>
              <Button
                size='sm'
                className='text-xs'
                onClick={handleSplitConfirm}
                disabled={splitLines.filter((l) => l.description.trim() && l.amount).length < 2 || Math.abs(splitTotal - Number(row.amount || 0)) >= 0.02}
                loading={loading?.split}
              >
                <Scissors size={13} strokeWidth={1.5} />Confirm Split & Match
              </Button>
              <Button
                variant='outline'
                size='sm'
                className='text-xs'
                onClick={() => {
                  const hasData = splitLines.some((l) => l.description.trim() || l.amount);
                  if (hasData && !window.confirm('Discard split lines? Any entered data will be lost.')) return;
                  setSplitInlineRow(null);
                  setSplitLines([]);
                }}
              >
                Cancel Split
              </Button>
            </div>
          </div>
        ) : null
    );

    return (
      <div
        className={`${embedded ? 'border-0 border-t border-border rounded-none' : 'border rounded-lg'} bg-card overflow-visible transition-all duration-500 ${
          isConfirmed || isCommitted ? 'opacity-60 border-l-[3px] border-l-success' : ''
        } ${isExcluded ? 'opacity-50' : ''} ${isCommitError ? 'border-l-[3px] border-l-danger bg-danger/5' : ''} ${
          isSplit ? 'border-l-[3px] border-l-amber-500' : ''
        } ${isDuplicateSkipped ? 'border-l-[3px] border-l-muted-foreground/60 bg-muted/10' : ''} ${
          flashMatchedDetail ? 'ring-2 ring-success/70 shadow-md shadow-success/15 bg-success-bg/20' : ''
        }`}
      >
        {compactLayout ? (
          <>
            <div className='p-4 bg-secondary/[0.02] relative z-0 overflow-visible'>
              {renderProposedUsage()}
            </div>
            <div className='flex items-center justify-between gap-2 px-4 py-2.5 border-t border-border flex-wrap'>
              {renderRowActions()}
            </div>
            {renderAlternatives()}
            {renderSplitEditor()}
          </>
        ) : (
          <>
            <div className='grid grid-cols-1 md:grid-cols-2'>
              <div className='p-4 border-b md:border-b-0 md:border-r border-border'>
                <p className='text-[10px] font-bold uppercase tracking-wider text-primary mb-2'>Source Transaction</p>
                <p className='text-sm font-semibold text-foreground whitespace-normal break-words leading-snug mb-1'>
                  {row.cleanDescription || row.rawDescription || row.description}
                </p>
                <div className='flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground'>
                  {txnDate && (
                    <span className='flex items-center gap-1'>
                      <Calendar size={11} strokeWidth={1.5} />
                      <span className='font-mono'>{txnDate}</span>
                    </span>
                  )}
                  <span className='font-mono font-semibold text-foreground'>
                    {row.currency || '£'}{Number(row.amount || 0).toFixed(2)}
                  </span>
                  {row.sourceKind && <span>{row.sourceKind}</span>}
                  {matchedFacilityName && (
                    <span className='flex items-center gap-1'>
                      <MapPin size={11} strokeWidth={1.5} />
                      <span>{matchedFacilityName}</span>
                    </span>
                  )}
                </div>
              </div>
              <div className='p-4 bg-secondary/[0.02] relative z-0 overflow-visible'>
                <p className='text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-2'>Proposed Usage Record</p>
                {renderProposedUsage()}
              </div>
            </div>
            <div className='flex items-center justify-between gap-2 px-4 py-2.5 border-t border-border flex-wrap'>
              {renderRowActions()}
            </div>
            {renderAlternatives()}
            {renderSplitEditor()}
          </>
        )}
      </div>
    );
  };

  /** Split child line editor; `embedded: true` when opened from compact accordion. */
  const splitChildRowDetail = (childRow, group, { embedded = false } = {}) => {
const childKey = childRow.id || childRow.stagingId;
const childMatches = matchMap[childKey] || [];
const childTopMatch = childMatches[0];
const childStatus = childRow.pipelineStatus || childRow.status;
const isChildConfirmed = childStatus === 'CONFIRMED';
const isChildExcluded = childStatus === 'EXCLUDED';
const isChildCommitted = childStatus === 'COMMITTED';
const isChildReviewable = !isChildConfirmed && !isChildExcluded && !['COMMIT_ERROR', 'COMMITTED', 'SPLIT'].includes(childStatus);
const childEmissions = computeSpendEmissions(childRow, childTopMatch, metricsById);
const childQualityTier = childTopMatch?.qualityTier || childTopMatch?.quality_tier;
const childUpgradeAvailable = childTopMatch?.qualityUpgradeAvailable || childTopMatch?.quality_upgrade_available;
const childEntities = childTopMatch?.entities || [];
const childChosenEntity = childEntities[0];

const openChildSearch = () => {
  const desc = childRow.cleanDescription || childRow.rawDescription || '';
  setSearchModal(childRow);
  setSearchQuery(desc);
  setAiSuggestResponse(null);
  setClarifyReplies({});
  setAlternatesOpen(false);
  setLocationOverride(null);
  if (desc.length >= 2) handleAiSuggest(desc);
};

const childEdit = splitChildEdits[childKey] || {
  description: childRow.cleanDescription || childRow.rawDescription || childRow.description || '',
  amount: String(childRow.amount || ''),
};
const updateChildEdit = (field, value) => setSplitChildEdits((prev) => ({
  ...prev,
  [childKey]: { ...prev[childKey] || { description: childRow.cleanDescription || childRow.rawDescription || '', amount: String(childRow.amount || '') }, [field]: value },
                        }));
const silverAlt = childUpgradeAvailable && childQualityTier === 'bronze'
  ? childMatches.slice(1).find((a) => !isSpendFactor(a, metricsById))
  : null;

return (
  <div className={`${embedded ? 'border-0 border-t border-border rounded-none' : 'border rounded-lg'} overflow-hidden ${isChildConfirmed || isChildCommitted ? 'opacity-60 border-l-[3px] border-l-success' : 'border-amber-300/40'} ${isChildExcluded ? 'opacity-50' : ''}`}>
    <div className='grid grid-cols-1 md:grid-cols-2'>
      <div className='p-3 bg-card'>
        <p className='text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5'>Source</p>
        <Input
          className='text-xs bg-background mb-1.5 h-8'
          value={childEdit.description}
          onChange={(e) => updateChildEdit('description', e.target.value)}
          disabled={isChildConfirmed || isChildCommitted || isChildExcluded}
        />
        <div className='flex gap-2 items-center'>
          <Input
            className='text-xs bg-background text-right font-mono w-24 h-8'
            type='number'
            step='0.01'
            value={childEdit.amount}
            onChange={(e) => updateChildEdit('amount', e.target.value)}
            disabled={isChildConfirmed || isChildCommitted || isChildExcluded}
          />
          <span className='text-xs text-muted-foreground'>{childRow.currency || group.parentCurrency} spend</span>
        </div>
      </div>
      <div className='p-3 bg-amber-50/20 dark:bg-amber-950/10 border-t md:border-t-0 md:border-l border-dashed border-amber-300/40 relative'>
        <div className='flex items-start justify-between'>
          <p className='text-[10px] text-amber-700 dark:text-amber-400 uppercase tracking-wider mb-1'>Usage Record</p>
          {isChildReviewable && (
            <Button
              variant='ghost'
              size='sm'
              className='h-5 w-5 p-0 text-muted-foreground hover:text-danger -mt-0.5 -mr-1'
              onClick={() => { setExcludeModal(childRow); setExcludeReason(EXCLUDE_REASONS[0].value); setExcludeNotes(''); }}
              title='Exclude this line'
            >
              <X size={12} strokeWidth={1.5} />
            </Button>
          )}
        </div>
        {childTopMatch ? (
          <>
            <p className='text-xs font-semibold text-foreground whitespace-normal break-words leading-snug'>{childTopMatch.factorName}</p>
            <p className='text-[10px] text-muted-foreground'>
              {childTopMatch.co2ePerUnit ? `${fmt2dp(childTopMatch.co2ePerUnit)} kgCO₂e/${getMetricLabel(childTopMatch, metricsById)}` : ''}
              {childTopMatch.subentity && !/^\d+$/.test(childTopMatch.subentity) ? ` · ${childTopMatch.subentity}` : ''}
            </p>
            {(() => {
              const cTopIsSpend = isSpendFactor(childTopMatch, metricsById);
              const cEffectiveAct = getEffectiveActivityDetectedAmount(childTopMatch, childRow, metricsById);
              const cDetected = cEffectiveAct;
              const cActAmt =
                activityAmounts[childKey] !== undefined
                  ? activityAmounts[childKey]
                  : cEffectiveAct != null
                    ? String(cEffectiveAct)
                    : '';
              if (cTopIsSpend) {
                return (
                  <div className='flex items-center gap-2 mt-1'>
                    <span className='text-[10px]'><QualityBadge level={qualityBadgeLevelForMatch(childTopMatch, childRow)} /></span>
                    {childEmissions && (
                      <span className='text-xs font-semibold text-foreground'>
                        {childRow.currency || group.parentCurrency}{Number(childRow.amount || 0).toFixed(2)} → {childEmissions} kgCO₂e
                      </span>
                    )}
                  </div>
                );
              }
              const cActEmissions = cActAmt ? (parseFloat(cActAmt) * (childTopMatch.co2ePerUnit || 0)).toFixed(1) : null;
              const cMetric = getMetricLabel(childTopMatch, metricsById);
              return (
                <div className='mt-1'>
                  <div className='flex items-center gap-1.5 flex-wrap'>
                    <span className='text-[10px]'><QualityBadge level={qualityBadgeLevelForMatch(childTopMatch, childRow)} /></span>
                    <Input
                      className='w-16 h-5 text-[10px] bg-background px-1 font-mono'
                      type='number'
                      step='any'
                      placeholder={cMetric}
                      value={cActAmt}
                      onChange={(e) => setActivityAmounts((prev) => ({ ...prev, [childKey]: e.target.value }))}
                    />
                    <span className='text-[10px] text-muted-foreground'>{cMetric} →</span>
                    <span className='text-[10px] font-semibold'>{cActEmissions ? `${cActEmissions} kgCO₂e` : '?'}</span>
                  </div>
                  {cDetected != null && cDetected !== '' && (
                    <p className='text-[9px] text-muted-foreground italic mt-0.5'>
                      Pre-filled from description ({cDetected} {cMetric})
                    </p>
                  )}
                  {formatOriginalFinancialContext(childRow) && (
                    <p className='text-[9px] text-muted-foreground mt-0.5'>{formatOriginalFinancialContext(childRow)}</p>
                  )}
                </div>
              );
            })()}
            {childChosenEntity && (
              <p className='text-[10px] text-muted-foreground mt-0.5'>
                Entity: {childChosenEntity.name || childChosenEntity.subentity}
              </p>
            )}
            {silverAlt && (
              <div className='flex items-center gap-1.5 mt-1.5 text-[10px] flex-wrap'>
                <span className='text-amber-600'>↑ Silver:</span>
                <span className='font-medium'>{fmt2dp(silverAlt.co2ePerUnit)} kgCO₂e/{getMetricLabel(silverAlt, metricsById)}</span>
                <Input
                  className='w-14 h-5 text-[10px] bg-background px-1'
                  placeholder='qty'
                  value={upgradeAmounts[childKey] || ''}
                  onChange={(e) => setUpgradeAmounts((prev) => ({ ...prev, [childKey]: e.target.value }))}
                />
                <Button variant='amber' size='sm' className='text-[10px] h-5 px-1.5' disabled={!upgradeAmounts[childKey]} onClick={() => handleConfirm(childRow, silverAlt)}>Apply</Button>
              </div>
            )}
            <div className='flex items-center gap-2 mt-1.5'>
              <Button variant='ghost' size='sm' className='h-5 text-[10px] px-1 text-muted-foreground' onClick={openChildSearch}>
                <Search size={10} strokeWidth={1.5} />Change factor
              </Button>
              {childMatches.length > 1 && (
                <Button variant='ghost' size='sm' className='h-5 text-[10px] px-1 text-muted-foreground' onClick={() => setAlternatesOpenRows((prev) => ({ ...prev, [childKey]: !prev[childKey] }))}>
                  {alternatesOpenRows[childKey] ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                  Alternatives
                </Button>
              )}
            </div>
            {alternatesOpenRows[childKey] && childMatches.length > 1 && (
              <div className='mt-2 pt-2 border-t border-amber-300/30'>
                {childMatches.slice(1).map((alt, ai) => {
                  const altKey = `${childKey}-alt-${ai}`;
                  const altIsSpend = isSpendFactor(alt, metricsById);
                  const altEmissions = altIsSpend ? computeSpendEmissions(childRow, alt, metricsById) : null;
                  const altSpend = altIsSpend ? resolveSpendForFactor(childRow, alt, metricsById) : null;
                  const altEff = getEffectiveActivityDetectedAmount(alt, childRow, metricsById);
                  const altAmount =
                    altActivityAmounts[altKey] !== undefined
                      ? altActivityAmounts[altKey]
                      : altEff != null
                        ? String(altEff)
                        : '';
                  const altCalcEmissions = !altIsSpend && altAmount ? (parseFloat(altAmount) * (alt.co2ePerUnit || 0)).toFixed(1) : null;
                  const altConfirmQty = altAmount.trim();
                  return (
                    <div key={altKey} className='py-1.5 border-b border-border last:border-0'>
                      <div className='flex items-center gap-2'>
                        <div className='flex-1 min-w-0'>
                          <p className='text-[11px] whitespace-normal break-words leading-snug'>{alt.factorName}</p>
                          <p className='text-[10px] text-muted-foreground'>
                            {alt.co2ePerUnit ? `${fmt2dp(alt.co2ePerUnit)} kgCO₂e/${getMetricLabel(alt, metricsById)}` : ''}
                          </p>
                        </div>
                        <QualityBadge level={qualityBadgeLevelForMatch(alt, childRow)} />
                        {altIsSpend && (
                          <Button variant='outline' size='sm' className='text-[10px] h-5 px-1.5' onClick={() => handleConfirm(childRow, alt)}>Use</Button>
                        )}
                      </div>
                      {altIsSpend && (
                        <div className='flex flex-col gap-0.5 mt-1 text-[10px] text-muted-foreground'>
                          <div className='flex items-center gap-1.5 flex-wrap'>
                            <Lock className='h-3 w-3 shrink-0' strokeWidth={1.5} />
                            <span className='text-foreground'>Spend:</span>
                            <span className='rounded border border-border bg-muted/40 px-1.5 py-0.5 font-mono tabular-nums text-foreground'>
                              {altSpend?.amount != null
                                ? `${symbolForCurrencyCode(altSpend.currencyCode)}${altSpend.amount.toFixed(2)}`
                                : '—'}
                            </span>
                            <span>→</span>
                            <span className='font-semibold text-info'>{altEmissions != null ? `${altEmissions} kgCO₂e` : '?'}</span>
                          </div>
                          {altSpend?.source === 'missing' ? (
                            <p className='text-[9px] text-warning pl-4'>No converted {altSpend.currencyCode} spend on this line.</p>
                          ) : null}
                        </div>
                      )}
                      {!altIsSpend && (
                        <div className='flex flex-col gap-0.5 mt-1 text-[10px] flex-wrap'>
                          <div className='flex items-center gap-1.5 flex-wrap'>
                            <span className='text-amber-700 font-semibold'>↑ {getMetricLabel(alt, metricsById)}:</span>
                            <Input className='w-14 h-5 text-[10px] bg-background px-1' placeholder={getMetricLabel(alt, metricsById)} value={altAmount} onChange={(e) => setAltActivityAmounts((prev) => ({ ...prev, [altKey]: e.target.value }))} />
                            <span className='text-muted-foreground'>→</span>
                            <span className='font-semibold text-info'>{altCalcEmissions ? `${altCalcEmissions} kgCO₂e` : '?'}</span>
                            <Button variant='amber' size='sm' className='text-[10px] h-5 px-1.5' disabled={!altConfirmQty} onClick={() => handleConfirm(childRow, alt, rememberChoice, undefined, altConfirmQty)}>Use</Button>
                          </div>
                          {formatOriginalFinancialContext(childRow) && (
                            <p className='text-[9px] text-muted-foreground'>{formatOriginalFinancialContext(childRow)}</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {isChildConfirmed && !isChildCommitted && (
              <div className='flex items-center gap-2 mt-1.5 flex-wrap'>
                <Badge className='bg-success-bg text-success text-[9px] px-1.5'>Confirmed</Badge>
                <Button variant='outline' size='sm' className='h-6 text-[10px] px-2' onClick={() => handleUnconfirm(childRow)} disabled={processing[childKey]}>
                  <Undo2 size={11} strokeWidth={1.5} />Edit / unconfirm
                </Button>
              </div>
            )}
            {isChildCommitted && <Badge className='bg-primary/10 text-primary text-[9px] px-1.5 mt-1.5'>Committed</Badge>}
            {isChildExcluded && <Badge className='bg-muted text-muted-foreground text-[9px] px-1.5 mt-1.5'>Excluded</Badge>}
          </>
        ) : (
          <>
            <p className='text-xs text-muted-foreground italic mb-1.5'>No conversion factor matched this line</p>
            <Button variant='ghost' size='sm' className='h-5 text-[10px] px-1 text-muted-foreground' onClick={openChildSearch}>
              <Search size={10} strokeWidth={1.5} />Find factor
            </Button>
          </>
        )}
      </div>
    </div>
  </div>
  );
  };


  return (
    <TooltipProvider>
      <div className='flex flex-col gap-4'>
        {(stats.commitErrors > 0 || jobStatus === 'PARTIAL_COMPLETE') && (
          <Alert variant='destructive' className='border-danger/30 bg-danger/5'>
            <AlertCircle size={16} />
            <AlertDescription>
              {stats.commitErrors} row{stats.commitErrors !== 1 ? 's' : ''} failed to commit.
              {stats.committed > 0 && ` ${stats.committed} row${stats.committed !== 1 ? 's were' : ' was'} committed successfully.`}
              {' '}You can change the factor on error rows and retry the commit.
            </AlertDescription>
          </Alert>
        )}
        {activityRequiredBanner ? (
          <Alert variant='destructive' className='border-warning/40 bg-warning-bg/20'>
            <AlertCircle size={16} />
            <AlertDescription className='text-foreground'>{activityRequiredBanner}</AlertDescription>
          </Alert>
        ) : null}

        <div className='grid grid-cols-2 md:grid-cols-5 gap-3'>
          <Card className='bg-card'>
            <CardContent className='p-4 text-center'>
              <p className='text-2xl font-bold'>{stats.total}</p>
              <p className='text-xs text-muted-foreground'>Total Rows</p>
            </CardContent>
          </Card>
          <Card className='bg-card'>
            <CardContent className='p-4 text-center'>
              <p className='text-2xl font-bold text-success'>{stats.confirmed}</p>
              <p className='text-xs text-success'>Confirmed</p>
            </CardContent>
          </Card>
          <Card className='bg-card'>
            <CardContent className='p-4 text-center'>
              <p className='text-2xl font-bold text-warning'>{stats.pending}</p>
              <p className='text-xs text-warning'>Pending</p>
            </CardContent>
          </Card>
          <Card className='bg-card'>
            <CardContent className='p-4 text-center'>
              <p className='text-2xl font-bold text-info'>{stats.highConf}</p>
              <p className='text-xs text-info'>High Confidence</p>
            </CardContent>
          </Card>
          {stats.committed > 0 && (
            <Card className='bg-card'>
              <CardContent className='p-4 text-center'>
                <p className='text-2xl font-bold text-primary'>{stats.committed}</p>
                <p className='text-xs text-primary'>Committed</p>
              </CardContent>
            </Card>
          )}
          {stats.commitErrors > 0 && (
            <Card className='bg-card border-danger/30'>
              <CardContent className='p-4 text-center'>
                <p className='text-2xl font-bold text-danger'>{stats.commitErrors}</p>
                <p className='text-xs text-danger'>Commit Errors</p>
              </CardContent>
            </Card>
          )}
          {stats.excluded > 0 && (
            <Card className='bg-card'>
              <CardContent className='p-4 text-center'>
                <p className='text-2xl font-bold text-muted-foreground'>{stats.excluded}</p>
                <p className='text-xs text-muted-foreground'>Excluded</p>
              </CardContent>
            </Card>
          )}
          {stats.exhausted > 0 && (
            <Card className='bg-card'>
              <CardContent className='p-4 text-center'>
                <p className='text-2xl font-bold text-danger'>{stats.exhausted}</p>
                <p className='text-xs text-danger'>Needs Attention</p>
              </CardContent>
            </Card>
          )}
          {duplicateSkippedCount > 0 && (
            <Card className={`bg-card ${showDuplicateRows ? 'ring-2 ring-primary/30' : ''}`}>
              <CardContent className='p-4 text-center'>
                <p className='text-2xl font-bold text-muted-foreground'>{duplicateSkippedCount}</p>
                <p className='text-xs text-muted-foreground'>Duplicates skipped</p>
              </CardContent>
            </Card>
          )}
        </div>

        <div className='flex items-center justify-between flex-wrap gap-2'>
          <div className='flex items-center gap-3'>
            <div className='flex items-center gap-2'>
              <Sparkles size={16} className='text-primary' strokeWidth={1.5} />
              <span className='text-sm text-muted-foreground'>
                AI has matched your transactions to carbon conversion factors
              </span>
            </div>
            <label className='flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none'>
              <input
                type='checkbox'
                checked={rememberChoice}
                onChange={(e) => setRememberChoice(e.target.checked)}
                className='accent-primary rounded'
              />
              Remember mappings for future imports
            </label>
          </div>
          <div className='flex gap-2 items-center'>
            <div className='inline-flex border border-border rounded-lg overflow-hidden bg-card'>
              <button
                type='button'
                className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors ${viewMode === 'detailed' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/50'}`}
                onClick={() => {
                  setViewMode('detailed');
                  setCompactExpandedKey(null);
                }}
              >
                <LayoutGrid size={12} strokeWidth={1.5} />Detailed
              </button>
              <button
                type='button'
                className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors border-l border-border ${viewMode === 'compact' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/50'}`}
                onClick={() => {
                  setViewMode('compact');
                  setCompactExpandedKey(null);
                }}
              >
                <LayoutList size={12} strokeWidth={1.5} />Compact
              </button>
            </div>
            <Button
              type='button'
              variant={showCompletedRows ? 'secondary' : 'outline'}
              size='sm'
              className='text-xs gap-1.5'
              onClick={() => setShowCompletedRows((v) => !v)}
              title={showCompletedRows ? 'Only show rows still to review' : 'Include confirmed and committed rows'}
            >
              {showCompletedRows ? <EyeOff size={14} strokeWidth={1.5} /> : <Eye size={14} strokeWidth={1.5} />}
              {showCompletedRows ? 'Hide completed' : 'Show completed'}
            </Button>
            {duplicateSkippedCount > 0 && (
              <Button
                type='button'
                variant={showDuplicateRows ? 'secondary' : 'outline'}
                size='sm'
                className='text-xs gap-1.5'
                onClick={() => setShowDuplicateRows((v) => !v)}
                title={showDuplicateRows ? 'Hide rows skipped by deduplication' : 'List rows skipped as duplicates of another line'}
              >
                <Copy size={14} strokeWidth={1.5} />
                {showDuplicateRows ? 'Hide duplicates' : `Show duplicates (${duplicateSkippedCount})`}
              </Button>
            )}
            {stats.highConf > 0 && (
              <Button
                variant='outline'
                size='sm'
                onClick={handleBulkConfirm}
                loading={processing.bulk}
              >
                <Check size={14} strokeWidth={1.5} />
                Confirm All High ({stats.highConf})
              </Button>
            )}
            {stats.pending === 0 && (stats.confirmed > 0 || stats.commitErrors > 0) && (
              <Button onClick={handleProceed} loading={loading?.commit}>
                {stats.commitErrors > 0 ? 'Retry Commit' : 'Proceed to Commit'}
                <ArrowRight size={14} strokeWidth={1.5} />
              </Button>
            )}
          </div>
        </div>

        {(reviewRows.length > 0 || stagingItems.length > 0) && (
          <p className='text-xs text-muted-foreground -mt-1'>
            <span className='text-muted-foreground'>
              Parsed <span className='font-semibold text-foreground'>{stagingItems.length}</span> row{stagingItems.length !== 1 ? 's' : ''}
              {duplicateSkippedCount > 0 && !showDuplicateRows && (
                <span> · {duplicateSkippedCount} duplicate{duplicateSkippedCount !== 1 ? 's' : ''} hidden</span>
              )}
              {duplicateSkippedCount > 0 && showDuplicateRows && (
                <span> · {duplicateSkippedCount} duplicate{duplicateSkippedCount !== 1 ? 's' : ''} listed</span>
              )}
            </span>
            {reviewRows.length > 0 && (
              <>
                {' · '}
                {!showCompletedRows ? (
                  <>
                    Showing <span className='font-semibold text-foreground'>{rowsForListView.length}</span> row{rowsForListView.length !== 1 ? 's' : ''} to review
                    {reviewRows.length > rowsForListView.length && (
                      <span> · {reviewRows.length - rowsForListView.length} completed hidden</span>
                    )}
                  </>
                ) : (
                  <>Showing all {reviewRows.length} row{reviewRows.length !== 1 ? 's' : ''}</>
                )}
              </>
            )}
          </p>
        )}

        {reviewRows.length === 0 && duplicateSkippedCount > 0 && !showDuplicateRows && (
          <Alert className='border-info/40 bg-info-bg/10'>
            <AlertCircle size={16} className='text-info' />
            <AlertDescription className='text-foreground'>
              All {duplicateSkippedCount} parsed row{duplicateSkippedCount !== 1 ? 's were' : ' was'} skipped as duplicates (dedup). Turn on{' '}
              <strong>Show duplicates ({duplicateSkippedCount})</strong> to list them, or open the Stage step for the full table.
            </AlertDescription>
          </Alert>
        )}

        {rowsForListView.length === 0 && reviewRows.length > 0 ? (
          <Card className='bg-card border-border'>
            <CardContent className='py-10 text-center'>
              <Check size={32} className='mx-auto mb-3 text-success' strokeWidth={1.5} />
              <p className='text-sm font-medium text-foreground'>Nothing left to review</p>
              <p className='text-xs text-muted-foreground mt-1 max-w-md mx-auto'>
                Confirmed and committed rows are hidden by default. Turn on <strong>Show completed</strong> to review them again, or continue when you are ready to commit.
              </p>
              <Button variant='outline' size='sm' className='mt-4' onClick={() => setShowCompletedRows(true)}>
                <Eye size={14} strokeWidth={1.5} /> Show completed rows
              </Button>
            </CardContent>
          </Card>
        ) : reviewRows.length === 0 ? (
          null
        ) : (
        <>
        {/* Compact view — fixed-height uniform rows */}
        {viewMode === 'compact' && (
          <div className='flex flex-col gap-1'>
            <div className='grid grid-cols-[1fr_1fr_140px] h-7 items-center px-3'>
              <span className='text-[10px] font-bold uppercase tracking-wider text-primary'>Source</span>
              <span className='text-[10px] font-bold uppercase tracking-wider text-amber-700'>Proposed Usage</span>
              <span className='text-[10px] font-bold uppercase tracking-wider text-muted-foreground text-right'>Actions</span>
            </div>
            {groupedReviewRows.map((group) => {
              if (group.type === 'split-group') {
                return (
                  <div key={`csg-${group.parentId}`} className='rounded-lg border border-amber-300 overflow-hidden'>
                    <div className='h-7 flex items-center gap-2 px-3 bg-amber-50 dark:bg-amber-950/30 text-[10px] text-amber-800 dark:text-amber-300'>
                      <Scissors size={10} strokeWidth={1.5} />
                      <span className='truncate'><strong>{group.parentDesc}</strong> · {group.parentCurrency}{Number(group.parentAmount || 0).toFixed(2)} → {group.children.length} items</span>
                    </div>
                    {group.children.map((row) => {
                      const key = row.id || row.stagingId;
                      const matches = matchMap[key] || [];
                      const topMatch = matches[0];
                      const rowStatus = row.pipelineStatus || row.status;
                      const isConfirmed = rowStatus === 'CONFIRMED';
                      const isExcluded = rowStatus === 'EXCLUDED';
                      const isCommitted = rowStatus === 'COMMITTED';
                      const isDuplicateSkipped = rowStatus === STAGING_ROW_STATUSES.DUPLICATE_SKIPPED;
                      const isReviewable = !isConfirmed && !isExcluded && !isDuplicateSkipped && !['COMMIT_ERROR', 'COMMITTED', 'SPLIT'].includes(rowStatus);
                      const emissions = computeSpendEmissions(row, topMatch, metricsById);
                      const pct = Math.round((topMatch?.confidence || 0) * 100);
                      const flashMatched = matchSuccessFlash[String(key)];
                      return (
                        <React.Fragment key={key}>
                          <div
                            className={`grid grid-cols-[1fr_1fr_140px] items-center h-12 border-t border-amber-200 bg-card overflow-hidden transition-all duration-500 ${
                              isConfirmed || isCommitted ? 'opacity-60 border-l-[3px] border-l-success' : ''
                            } ${isExcluded ? 'opacity-50' : ''} ${isDuplicateSkipped ? 'border-l-[3px] border-l-muted-foreground/50 bg-muted/15' : ''} ${
                              flashMatched ? 'ring-2 ring-inset ring-success bg-success-bg/25' : ''
                            }`}
                          >
                          <div className='px-3 overflow-hidden'>
                            <p className='text-xs font-semibold truncate'>{row.cleanDescription || row.rawDescription || row.description}</p>
                            <p className='text-[10px] text-muted-foreground font-mono truncate'>{row.currency || group.parentCurrency}{Number(row.amount || 0).toFixed(2)}</p>
                          </div>
                          <div className='px-3 border-l border-amber-200 overflow-hidden'>
                            {isDuplicateSkipped ? (
                              <p className='text-[10px] text-muted-foreground italic'>Duplicate — skipped</p>
                            ) : topMatch ? (
                              <>
                                <p className='text-xs font-semibold truncate'>{topMatch.factorName}</p>
                                <p className='text-[10px] text-muted-foreground truncate'>
                                  <QualityBadge level={qualityBadgeLevelForMatch(topMatch, row)} /> {emissions ? `${emissions} kgCO₂e · ` : ''}{pct}%
                                </p>
                              </>
                            ) : (
                              <p className='text-xs text-muted-foreground italic'>No factor</p>
                            )}
                          </div>
                          <div className='px-2 border-l border-amber-200 flex items-center justify-end gap-1'>
                            {isDuplicateSkipped && <Badge className='bg-muted text-muted-foreground text-[9px] px-1.5'>Dup</Badge>}
                            {isReviewable && topMatch && (() => {
                              const sgNonSpend = !isSpendFactor(topMatch, metricsById);
                              const sgEff = getEffectiveActivityDetectedAmount(topMatch, row, metricsById);
                              const sgResolved =
                                (activityAmounts[key] !== undefined && activityAmounts[key] !== ''
                                  ? activityAmounts[key]
                                  : sgEff != null
                                    ? String(sgEff)
                                    : '').trim();
                              const sgNeedsAmt = sgNonSpend && !sgResolved;
                              return (
                                <>
                                  <Button
                                    variant='default'
                                    size='sm'
                                    className='h-6 w-6 p-0'
                                    onClick={() => handleConfirm(row, topMatch)}
                                    disabled={processing[key] || sgNeedsAmt}
                                    title={sgNeedsAmt ? 'Enter activity amount in expanded details' : ''}
                                  >
                                    <Check size={12} strokeWidth={2} />
                                  </Button>
                                  <Button variant='outline' size='sm' className='h-6 w-6 p-0 text-danger border-danger/30' onClick={() => handleReject(row, topMatch)} disabled={processing[key]}>
                                    <X size={12} strokeWidth={2} />
                                  </Button>
                                  <Button
                                    variant='ghost'
                                    size='sm'
                                    className='h-6 px-1 text-[10px]'
                                    type='button'
                                    onClick={() => toggleCompactRowExpand(key)}
                                    aria-expanded={String(compactExpandedKey) === String(key)}
                                    title={sgNeedsAmt ? 'Enter activity amount in expanded details' : 'Show or hide full line details'}
                                  >
                                    {String(compactExpandedKey) === String(key) ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                                  </Button>
                                </>
                              );
                            })()}
                            {isConfirmed && (
                              <>
                                <Badge className='bg-success-bg text-success text-[9px] px-1.5'>✓</Badge>
                                <Button variant='ghost' size='sm' className='h-6 w-6 p-0' title='Edit / unconfirm' onClick={() => handleUnconfirm(row)} disabled={processing[key]}>
                                  <Undo2 size={12} strokeWidth={1.5} />
                                </Button>
                              </>
                            )}
                            {isCommitted && <Badge className='bg-primary/10 text-primary text-[9px] px-1.5'>Done</Badge>}
                            {isExcluded && <Badge className='bg-muted text-muted-foreground text-[9px] px-1.5'>Excl</Badge>}
                          </div>
                        </div>
                          {String(compactExpandedKey) === String(key) && (
                            <div ref={compactAccordionRef} className='border-t border-amber-200 bg-card overflow-visible'>
                              {splitChildRowDetail(row, group, { embedded: true })}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                );
              }

              const row = group.row;
              const key = row.id || row.stagingId;
              const matches = matchMap[key] || [];
              const topMatch = matches[0];
              const rowStatus = row.pipelineStatus || row.status;
              const isConfirmed = rowStatus === 'CONFIRMED';
              const isExcluded = rowStatus === 'EXCLUDED';
              const isCommitted = rowStatus === 'COMMITTED';
              const isSplit = rowStatus === 'SPLIT';
              const isDuplicateSkipped = rowStatus === STAGING_ROW_STATUSES.DUPLICATE_SKIPPED;
              const isReviewable = !isConfirmed && !isExcluded && !isDuplicateSkipped && !['COMMIT_ERROR', 'COMMITTED', 'SPLIT'].includes(rowStatus);
              const txnDate = row.transactionDate || row.transaction_date || row.date;
              const qualityTier = topMatch?.qualityTier || topMatch?.quality_tier;
              const upgradeAvailable = topMatch?.qualityUpgradeAvailable || topMatch?.quality_upgrade_available;
              const compactActivityUpgrade = parseActivityUpgrade(topMatch);
              const compactPremiumUpgrade = parsePremiumUpgrade(topMatch);
              const emissions = computeSpendEmissions(row, topMatch, metricsById);
              const pct = Math.round((topMatch?.confidence || 0) * 100);
              const compactPeriodLabel = pickFirst(row, 'detectedPeriodLabel', 'detected_period_label') || pickFirst(topMatch, 'detectedPeriodLabel', 'detected_period_label');
              const compactFacility = pickFirst(row, 'matchedFacility', 'matched_facility') || pickFirst(topMatch, 'matchedFacility', 'matched_facility');
              const flashMatchedCompact = matchSuccessFlash[String(key)];

              if (isSplit) return null;

              return (
                <React.Fragment key={key}>
                  <div
                    className={`grid grid-cols-[1fr_1fr_140px] items-center h-12 border rounded-lg bg-card overflow-hidden transition-all duration-500 ${
                      isConfirmed || isCommitted ? 'opacity-60 border-l-[3px] border-l-success' : ''
                    } ${isExcluded ? 'opacity-50' : ''} ${isDuplicateSkipped ? 'border-l-[3px] border-l-muted-foreground/50 bg-muted/15' : ''} ${
                      flashMatchedCompact ? 'ring-2 ring-inset ring-success bg-success-bg/25' : ''
                    }`}
                  >
                  <div className='px-3 overflow-hidden'>
                    <p className='text-xs font-semibold truncate'>{row.cleanDescription || row.rawDescription || row.description}</p>
                    <p className='text-[10px] text-muted-foreground font-mono truncate'>
                      {txnDate || ''} · {row.currency || '£'}{Number(row.amount || 0).toFixed(2)}
                      {compactPeriodLabel ? ` · ${compactPeriodLabel}` : ''}
                      {compactFacility ? ` · ${compactFacility}` : ''}
                    </p>
                  </div>
                  <div className='px-3 border-l border-border overflow-hidden'>
                    {isDuplicateSkipped ? (
                      <p className='text-[10px] text-muted-foreground italic'>Duplicate — skipped</p>
                    ) : topMatch ? (
                      <>
                        <p className='text-xs font-semibold truncate'>{topMatch.factorName}</p>
                        <p className='text-[10px] text-muted-foreground truncate'>
                          <QualityBadge level={qualityBadgeLevelForMatch(topMatch, row)} />{' '}
                          {emissions ? `${emissions} kgCO₂e · ` : ''}{pct}%
                          {(compactActivityUpgrade || (upgradeAvailable && qualityTier === 'bronze')) && <span className='text-amber-600 ml-1'>↑ Silver</span>}
                          {compactPremiumUpgrade && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type='button'
                                  className='inline-flex items-center gap-0.5 text-primary ml-1 hover:underline'
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openUpgradeDialog(compactPremiumUpgrade.required_add_on_id, compactPremiumUpgrade.add_on_name);
                                  }}
                                >
                                  <Lock className='h-2.5 w-2.5' strokeWidth={1.5} />
                                  <span className='text-[9px]'>Better match</span>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {compactPremiumUpgrade.factor_name || 'A better match'} — upgrade to {compactPremiumUpgrade.add_on_name || 'a premium add-on'}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </p>
                      </>
                    ) : rematchingRows[String(key)] ? (
                      <p className='text-xs text-muted-foreground italic'>Finding a factor…</p>
                    ) : (
                      <p className='text-xs text-muted-foreground italic'>No factor</p>
                    )}
                  </div>
                  <div className='px-2 border-l border-border flex items-center justify-end gap-1'>
                    {isDuplicateSkipped && <Badge className='bg-muted text-muted-foreground text-[9px] px-1.5'>Dup</Badge>}
                    {isReviewable && topMatch && (() => {
                      const cNonSpend = !isSpendFactor(topMatch, metricsById);
                      const cEffAct = getEffectiveActivityDetectedAmount(topMatch, row, metricsById);
                      const cResolvedAct =
                        (activityAmounts[key] !== undefined && activityAmounts[key] !== ''
                          ? activityAmounts[key]
                          : cEffAct != null
                            ? String(cEffAct)
                            : '').trim();
                      const cNeedsAmt = cNonSpend && !cResolvedAct;
                      return (
                        <>
                          <Button variant='default' size='sm' className='h-6 w-6 p-0' onClick={() => handleConfirm(row, topMatch)} disabled={processing[key] || cNeedsAmt} title={cNeedsAmt ? 'Enter activity amount in expanded details' : ''}>
                            <Check size={12} strokeWidth={2} />
                          </Button>
                          <Button variant='outline' size='sm' className='h-6 w-6 p-0 text-danger border-danger/30' onClick={() => handleReject(row, topMatch)} disabled={processing[key]}>
                            <X size={12} strokeWidth={2} />
                          </Button>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-6 px-1 text-[10px]'
                            type='button'
                            onClick={() => toggleCompactRowExpand(key)}
                            aria-expanded={String(compactExpandedKey) === String(key)}
                            title={cNeedsAmt ? 'Enter activity amount in expanded details' : 'Show or hide full row details'}
                          >
                            {String(compactExpandedKey) === String(key) ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                          </Button>
                        </>
                      );
                    })()}
                    {isConfirmed && (
                      <>
                        <Badge className='bg-success-bg text-success text-[9px] px-1.5'>✓</Badge>
                        <Button variant='ghost' size='sm' className='h-6 w-6 p-0' title='Edit / unconfirm' onClick={() => handleUnconfirm(row)} disabled={processing[key]}>
                          <Undo2 size={12} strokeWidth={1.5} />
                        </Button>
                      </>
                    )}
                    {isCommitted && <Badge className='bg-primary/10 text-primary text-[9px] px-1.5'>Done</Badge>}
                    {isExcluded && <Badge className='bg-muted text-muted-foreground text-[9px] px-1.5'>Excl</Badge>}
                  </div>
                </div>
                  {String(compactExpandedKey) === String(key) && (
                    <div ref={compactAccordionRef} className='mt-0 border border-t-0 rounded-b-lg border-border bg-card overflow-visible'>
                      {topLevelRowDetail(row, { embedded: true, compactLayout: true })}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* Detailed view — full split-pane reconciliation */}
        {viewMode === 'detailed' && (
        <div className='flex flex-col gap-3'>
          {groupedReviewRows.map((group) => {
            if (group.type === 'split-group') {
              const parentTxnDate = group.parentDate;
              return (
                <div key={`sg-${group.parentId}`} className='border rounded-lg bg-card overflow-hidden'>
                  <div className='p-4 border-b border-border'>
                    <p className='text-[10px] font-bold uppercase tracking-wider text-primary mb-2'>Source Transaction</p>
                    <p className='text-sm font-semibold text-foreground whitespace-normal break-words leading-snug mb-1'>{group.parentDesc}</p>
                    <div className='flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground'>
                      {parentTxnDate && (
                        <span className='flex items-center gap-1'>
                          <Calendar size={11} strokeWidth={1.5} />
                          <span className='font-mono'>{parentTxnDate}</span>
                        </span>
                      )}
                      <span className='font-mono font-semibold text-foreground'>{group.parentCurrency}{Number(group.parentAmount || 0).toFixed(2)}</span>
                    </div>
                  </div>

                  <div className='border-t-2 border-dashed border-amber-400 px-4 py-3 bg-amber-50/30 dark:bg-amber-950/20'>
                    <p className='text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-3'>
                      <Scissors size={12} className='inline mr-1' strokeWidth={1.5} />
                      Split into {group.children.length} line items
                    </p>

                    <div className='flex flex-col gap-2'>
                      {group.children.map((childRow) => (
                        <React.Fragment key={childRow.id || childRow.stagingId}>
                          {splitChildRowDetail(childRow, group, { embedded: false })}
                        </React.Fragment>
                      ))}
                    </div>

                    {(() => {
                      const childTotal = group.children.reduce((s, c) => {
                        const edited = splitChildEdits[c.id || c.stagingId];
                        return s + (parseFloat(edited?.amount ?? c.amount) || 0);
                      }, 0);
                      const combinedEmissions = group.children.reduce((s, c) => {
                        const cKey = c.id || c.stagingId;
                        const m = matchMap[cKey]?.[0];
                        if (!m) return s;
                        const mIsSpend = isSpendFactor(m, metricsById);
                        if (mIsSpend) {
                          const se = computeSpendEmissions(c, m, metricsById);
                          return s + (se != null ? se : 0);
                        }
                        const eff = getEffectiveActivityDetectedAmount(m, c, metricsById);
                        const actAmt = parseFloat(
                          (activityAmounts[cKey] !== undefined && activityAmounts[cKey] !== ''
                            ? activityAmounts[cKey]
                            : eff != null
                              ? String(eff)
                              : '0') || '0',
                        );
                        return s + (actAmt * (m.co2ePerUnit || 0));
                      }, 0);
                      const parentAmt = Number(group.parentAmount || 0);
                      const balanced = Math.abs(childTotal - parentAmt) < 0.02;
                      const reviewableChildren = group.children.filter((c) => {
                        const s = c.pipelineStatus || c.status;
                        return !['CONFIRMED', 'EXCLUDED', 'COMMITTED', 'COMMIT_ERROR', 'SPLIT'].includes(s);
                      });
                      const allHaveMatch = reviewableChildren.every((c) => (matchMap[c.id || c.stagingId] || []).length > 0);
                      const allHaveActivity = reviewableChildren.every((c) => {
                        const m = matchMap[c.id || c.stagingId]?.[0];
                        if (!m || isSpendFactor(m, metricsById)) return true;
                        const eff = getEffectiveActivityDetectedAmount(m, c, metricsById);
                        const typed = (activityAmounts[c.id || c.stagingId] || '').trim();
                        return Boolean(typed || eff != null);
                      });
                      const canConfirmAll = reviewableChildren.length > 0 && allHaveMatch && allHaveActivity;
                      const allConfirmed = group.children.every((c) => ['CONFIRMED', 'COMMITTED'].includes(c.pipelineStatus || c.status));
                      const confirmedOnlyChildren = group.children.filter((c) => (c.pipelineStatus || c.status) === 'CONFIRMED');
                      const anyProcessing = group.children.some((c) => processing[c.id || c.stagingId]);

                      return (
                        <>
                          <div className='flex items-center justify-between mt-3 pt-2 border-t border-border text-sm'>
                            <span className='font-semibold'>
                              Total: {group.parentCurrency}{childTotal.toFixed(2)} of {group.parentCurrency}{parentAmt.toFixed(2)}
                              {combinedEmissions > 0 && <span className='text-muted-foreground font-normal'> · Combined: {combinedEmissions.toFixed(1)} kgCO₂e</span>}
                            </span>
                            {balanced ? (
                              <span className='text-xs text-success'>✓ Balanced</span>
                            ) : (
                              <span className='text-xs text-danger'>
                                {childTotal > parentAmt ? 'Over' : 'Under'} by {group.parentCurrency}{Math.abs(childTotal - parentAmt).toFixed(2)}
                              </span>
                            )}
                          </div>
                          {allConfirmed ? (
                            <div className='mt-2 flex flex-wrap items-center gap-2'>
                              <Badge className='bg-success-bg text-success'>All lines confirmed</Badge>
                              {confirmedOnlyChildren.length > 0 && (
                                <Button
                                  variant='outline'
                                  size='sm'
                                  className='text-xs h-7'
                                  disabled={anyProcessing}
                                  onClick={async () => {
                                    for (const c of confirmedOnlyChildren) {
                                      await handleUnconfirm(c);
                                    }
                                  }}
                                >
                                  <Undo2 size={12} strokeWidth={1.5} />Unconfirm all ({confirmedOnlyChildren.length})
                                </Button>
                              )}
                            </div>
                          ) : (
                            <div className='flex gap-2 mt-3'>
                              <Button
                                size='sm'
                                className='text-xs'
                                disabled={!canConfirmAll || anyProcessing}
                                onClick={async () => {
                                  for (const c of reviewableChildren) {
                                    const m = matchMap[c.id || c.stagingId]?.[0];
                                    if (m) await handleConfirm(c, m);
                                  }
                                }}
                                loading={anyProcessing}
                              >
                                <Check size={13} strokeWidth={2} />Confirm All ({reviewableChildren.length})
                              </Button>
                              {!allHaveMatch && <span className='text-xs text-muted-foreground self-center'>Some lines have no matched factor</span>}
                              {allHaveMatch && !allHaveActivity && <span className='text-xs text-amber-600 self-center'>Enter activity amounts for all lines first</span>}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              );
            }

            const groupRows = [group.row];
            const groupKey = groupRows[0]?.id || groupRows[0]?.stagingId;
            return (
              <div key={groupKey}>
                {groupRows.map((row) => (
                  <React.Fragment key={row.id || row.stagingId}>
                    {topLevelRowDetail(row, { embedded: false })}
                  </React.Fragment>
                ))}
              </div>
            );
          })}
        </div>
        )}
        </>
        )}
      </div>

      <Dialog open={!!rejectModal} onOpenChange={(open) => !open && setRejectModal(null)}>
        <DialogContent className='sm:max-w-sm bg-popover text-popover-foreground'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2 text-foreground'>
              <X size={16} strokeWidth={1.5} className='text-danger' />
              Reject Suggestion
            </DialogTitle>
          </DialogHeader>
          <DialogBody className='flex flex-col gap-3'>
            {rejectModal && (
              <div className='rounded-md bg-muted/50 px-3 py-2'>
                <p className='text-sm text-foreground truncate'>{rejectModal.match?.factorName || rejectModal.match?.factor_name}</p>
                <p className='text-xs text-muted-foreground'>for: {rejectModal.row?.cleanDescription || rejectModal.row?.rawDescription}</p>
              </div>
            )}
            <p className='text-sm font-medium text-foreground'>Why is this wrong?</p>
            <div className='flex flex-col gap-1'>
              {REJECT_REASONS.map((r) => (
                <label
                  key={r.value}
                  className={`flex items-center gap-2.5 text-sm cursor-pointer rounded-md px-3 py-1.5 transition-colors ${rejectReason === r.value ? 'bg-primary/10 text-foreground' : 'hover:bg-muted/50 text-muted-foreground'}`}
                >
                  <input
                    type='radio'
                    name='rejectReason'
                    checked={rejectReason === r.value}
                    onChange={() => setRejectReason(r.value)}
                    className='accent-primary'
                  />
                  {r.label}
                </label>
              ))}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant='outline' onClick={() => setRejectModal(null)}>Cancel</Button>
            <Button variant='destructive' onClick={handleRejectConfirm}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!excludeModal} onOpenChange={(open) => !open && setExcludeModal(null)}>
        <DialogContent className='sm:max-w-md bg-popover text-popover-foreground'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2 text-foreground'>
              <Ban size={16} strokeWidth={1.5} className='text-muted-foreground' />
              Exclude Row
            </DialogTitle>
          </DialogHeader>
          <DialogBody className='flex flex-col gap-4'>
            <div className='rounded-md bg-muted/50 px-3 py-2'>
              <p className='text-sm font-medium text-foreground'>{excludeModal?.cleanDescription || excludeModal?.rawDescription}</p>
              <p className='text-xs text-muted-foreground'>{excludeModal?.currency || '£'}{Number(excludeModal?.amount || 0).toFixed(2)}</p>
            </div>
            <div>
              <p className='text-sm font-medium text-foreground mb-2'>Why should this row be excluded?</p>
              <div className='flex flex-col gap-1.5'>
                {EXCLUDE_REASONS.map((reason) => (
                  <label
                    key={reason.value}
                    className={`flex items-center gap-2.5 text-sm cursor-pointer rounded-md px-3 py-2 transition-colors ${excludeReason === reason.value ? 'bg-primary/10 text-foreground' : 'hover:bg-muted/50 text-muted-foreground'}`}
                  >
                    <input
                      type='radio'
                      name='excludeReason'
                      checked={excludeReason === reason.value}
                      onChange={() => setExcludeReason(reason.value)}
                      className='accent-primary'
                    />
                    {reason.label}
                  </label>
                ))}
              </div>
            </div>
            <Input
              placeholder='Additional notes (optional)'
              value={excludeNotes}
              onChange={(e) => setExcludeNotes(e.target.value)}
              className='bg-background'
            />
            <label className='flex items-start gap-2.5 rounded-md bg-muted/30 p-3 cursor-pointer'>
              <input
                type='checkbox'
                checked={rememberExclusion}
                onChange={(e) => setRememberExclusion(e.target.checked)}
                className='accent-primary mt-0.5'
              />
              <div>
                <p className='text-sm font-medium text-foreground'>Remember this exclusion</p>
                <p className='text-xs text-muted-foreground'>
                  Transactions matching "{excludeModal?.cleanDescription || excludeModal?.rawDescription}" will be auto-excluded in future imports of the same source type
                </p>
              </div>
            </label>
          </DialogBody>
          <DialogFooter>
            <Button variant='outline' onClick={() => setExcludeModal(null)}>Cancel</Button>
            <Button variant='destructive' onClick={handleExclude}>Exclude</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!searchModal} onOpenChange={(open) => { if (!open) { setSearchModal(null); setAiSuggestResponse(null); setClarifyReplies({}); setAlternatesOpen(false); setLocationOverride(null); setSearchModalActivity(''); setSearchModalActivityError(''); } }}>
        <DialogContent className='sm:max-w-lg bg-popover text-popover-foreground max-h-[80vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2 text-foreground'>
              <Sparkles className='h-4 w-4 text-primary' strokeWidth={1.5} />
              Find Emission Factor
            </DialogTitle>
          </DialogHeader>
          <DialogBody className='flex flex-col gap-4'>
            <div className='rounded-md bg-muted/50 px-3 py-2'>
              <p className='text-sm font-medium text-foreground'>{searchModal?.cleanDescription || searchModal?.rawDescription}</p>
              <p className='text-xs text-muted-foreground'>{searchModal?.currency || '£'}{Number(searchModal?.amount || 0).toFixed(2)}</p>
            </div>

            <div className='rounded-md bg-muted/30 p-4'>
              <p className='text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5'>
                <Sparkles className='h-3.5 w-3.5 text-primary' strokeWidth={1.5} />
                Describe the activity
              </p>
              <textarea
                className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1'
                rows={2}
                placeholder='e.g. Uber taxi ride, office electricity bill, paper for printer...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleAiSuggest(searchQuery))}
              />
              {aiSuggestResponse?.suggest_status !== 'clarify' && (
                <div className='mt-3'>
                  <Button
                    size='sm'
                    onClick={() => handleAiSuggest(searchQuery)}
                    disabled={aiSuggestLoading || searchQuery.length < 2}
                    loading={aiSuggestLoading}
                  >
                    <Sparkles className='mr-1.5 h-3.5 w-3.5' strokeWidth={1.5} />
                    Suggest factor
                  </Button>
                </div>
              )}
            </div>

            {aiSuggestResponse?.suggest_status === 'clarify' && aiSuggestResponse?.clarify_items?.length > 0 && (
              <div className='rounded-md bg-info-bg/20 p-4'>
                <p className='text-xs font-semibold text-foreground mb-3'>Need a bit more detail:</p>
                <div className='flex flex-col gap-3'>
                  {aiSuggestResponse.clarify_items.map((ci, i) => (
                    <div key={ci.id || i}>
                      <label className='text-sm font-medium text-foreground'>{ci.label || ci.question}</label>
                      {ci.options?.length > 0 ? (
                        <div className='flex flex-wrap gap-1.5 mt-1.5'>
                          {ci.options.map((opt) => (
                            <Button
                              key={opt.value || opt}
                              variant={clarifyReplies[ci.id] === (opt.value || opt) ? 'default' : 'outline'}
                              size='sm'
                              className='text-xs'
                              onClick={() => setClarifyReplies((prev) => ({ ...prev, [ci.id]: opt.value || opt }))}
                            >
                              {opt.label || opt}
                            </Button>
                          ))}
                        </div>
                      ) : (
                        <Input
                          className='mt-1.5 bg-background'
                          placeholder={ci.placeholder || 'Type your answer...'}
                          value={clarifyReplies[ci.id] || ''}
                          onChange={(e) => setClarifyReplies((prev) => ({ ...prev, [ci.id]: e.target.value }))}
                        />
                      )}
                    </div>
                  ))}
                  <Button size='sm' className='self-start' onClick={() => handleAiSuggest(searchQuery, true)} loading={aiSuggestLoading}>
                    Submit answers
                  </Button>
                </div>
              </div>
            )}

            {aiSuggestResponse && aiSuggestResponse.suggest_status !== 'clarify' && (
              <>
                {(aiSuggestResponse.best || aiSuggestResponse.alternates?.length > 0) && (
                  <label className='flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none'>
                    <input
                      type='checkbox'
                      checked={rememberChoice}
                      onChange={(e) => setRememberChoice(e.target.checked)}
                      className='h-3.5 w-3.5 rounded border-border accent-primary'
                    />
                    Remember this choice for future matching
                  </label>
                )}

                {searchModal && (() => {
                  const row = searchModal;
                  const candidates = [...(aiSuggestResponse.best ? [aiSuggestResponse.best] : []), ...(aiSuggestResponse.alternates || [])];
                  const hasNonSpend = candidates.some((c) => c && !isSpendFactor(candidateToMatch(c, row), metricsById));
                  if (!hasNonSpend) return null;
                  const labelMatch = candidates.map((c) => candidateToMatch(c, row)).find((m) => !isSpendFactor(m, metricsById));
                  return (
                    <div className='rounded-md border border-border bg-muted/20 p-3'>
                      <label className='text-xs font-semibold text-foreground' htmlFor='search-modal-activity'>Activity amount</label>
                      <p className='text-xs text-muted-foreground mb-1.5'>
                        Required before confirming a non-spend factor ({getMetricLabel(labelMatch, metricsById)}).
                      </p>
                      <Input
                        id='search-modal-activity'
                        className='bg-background'
                        type='text'
                        inputMode='decimal'
                        placeholder={getMetricLabel(labelMatch, metricsById)}
                        value={searchModalActivity}
                        onChange={(e) => {
                          setSearchModalActivity(e.target.value);
                          setSearchModalActivityError('');
                        }}
                      />
                      {aiSuggestResponse?.usage?.amount != null && (
                        <p className='text-[10px] text-muted-foreground mt-1 italic'>
                          Pre-filled: {aiSuggestResponse.usage.amount} {aiSuggestResponse.usage.unit || getMetricLabel(labelMatch, metricsById)}
                          {aiSuggestResponse.usage_derivation?.conversions?.[0]?.rule_id
                            ? ` (${aiSuggestResponse.usage_derivation.conversions[0].rule_id.replace(/_/g, ' ')})`
                            : ' (calculated from description)'}
                        </p>
                      )}
                      {searchModalActivityError ? (
                        <p className='text-xs text-destructive mt-1.5'>{searchModalActivityError}</p>
                      ) : null}
                    </div>
                  );
                })()}

                {aiSuggestResponse.best && (() => {
                  const best = aiSuggestResponse.best;
                  const isLocked = best.accessible === false;
                  return (
                    <div className={`flex flex-col gap-2 rounded-md border p-3 ${isLocked ? 'border-warning/50 bg-warning-bg/10' : 'border-border bg-card'}`}>
                      <p className='text-xs text-muted-foreground'>Top match</p>
                      <p className='text-sm text-card-foreground'>
                        {best.factor_display_name || best.factor_name}
                      </p>
                      {best.signals?.llm_confidence != null && (
                        <p className='text-xs text-muted-foreground'>
                          Confidence: {Math.round((best.signals.llm_confidence || 0) * 100)}%
                        </p>
                      )}
                      {best.entities?.length > 0 && (
                        <p className='text-xs text-muted-foreground'>
                          Category: {best.entities.map((e) => e.name).join(', ')}
                          {best.entities.length > 1 && <span className='text-info ml-1'>(you will choose on confirm)</span>}
                        </p>
                      )}

                      {isLocked && (
                        <div className='rounded-md bg-warning-bg/30 px-3 py-2'>
                          <p className='text-xs font-medium text-warning flex items-center gap-1.5'>
                            <Lock className='h-3 w-3' strokeWidth={1.5} />
                            Premium dataset required
                          </p>
                          <p className='text-xs text-muted-foreground mt-1'>
                            This factor requires a data add-on not currently active on your account.
                            {best.available_locations?.length > 0
                              ? ' Try selecting a different location below that may be included in your plan, or upgrade to access this dataset.'
                              : ' Upgrade to access this dataset, or choose an alternative factor below.'}
                          </p>
                          {best.required_add_on_id ? (
                            <Button
                              variant='outline'
                              size='sm'
                              className='mt-2 text-xs border-primary/50 text-primary hover:bg-primary/10'
                              onClick={() => openUpgradeDialog(best.required_add_on_id, best.add_on_name)}
                            >
                              Upgrade to {best.add_on_name || 'premium'}
                            </Button>
                          ) : (
                            <Button
                              variant='outline'
                              size='sm'
                              className='mt-2 text-xs border-warning/50 text-warning hover:bg-warning/10'
                              onClick={() => {
                                const factorName = best.factor_display_name || best.factor_name || '';
                                const subject = encodeURIComponent('Data add-on upgrade request');
                                const body = encodeURIComponent(
                                  `Hi,\n\nI'd like to enquire about upgrading my account to access the following emission factor dataset:\n\n` +
                                  `Factor: ${factorName}\n\nPlease let me know the available options.\n\nThank you`,
                                );
                                window.open(`mailto:support@compareyourfootprint.com?subject=${subject}&body=${body}`);
                              }}
                            >
                              Contact support to upgrade
                            </Button>
                          )}
                        </div>
                      )}

                      {best.location_auto_defaulted && best.available_locations?.length > 0 && (() => {
                        const locs = best.available_locations;
                        const currentLoc = locationOverride || best.factor_dimensions?.finallocation;
                        return (
                          <div className='flex flex-col gap-1.5'>
                            <div className='flex items-center gap-1.5 text-xs text-muted-foreground'>
                              <MapPin className='h-3 w-3' strokeWidth={1.5} />
                              <span>Location:</span>
                            </div>
                            {locs.length <= 8 ? (
                              <div className='flex flex-wrap gap-1.5'>
                                {locs.map((loc) => (
                                  <Button
                                    key={loc.id}
                                    variant={loc.id === currentLoc ? 'default' : 'outline'}
                                    size='sm'
                                    className='text-xs'
                                    onClick={() => handleLocationChange(loc.id)}
                                    disabled={aiSuggestLoading}
                                  >
                                    {loc.name}
                                  </Button>
                                ))}
                              </div>
                            ) : (
                              <NewSelect
                                searchable
                                clearable={false}
                                size='sm'
                                placeholder='Select country of supply'
                                searchPlaceholder='Search countries...'
                                maxHeight='220px'
                                className='max-w-xs'
                                value={currentLoc}
                                options={locs.map((loc) => ({ value: loc.id, label: loc.name }))}
                                onChange={(val) => val && handleLocationChange(val)}
                                disabled={aiSuggestLoading}
                              />
                            )}
                          </div>
                        );
                      })()}

                      {!isLocked && (
                        <Button size='sm' className='self-start' onClick={() => handleSelectCandidate(best)}>
                          Use this suggestion
                        </Button>
                      )}
                    </div>
                  );
                })()}

                {aiSuggestResponse.alternates?.length > 0 && (
                  <div className='space-y-2'>
                    <button
                      type='button'
                      className='flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground'
                      onClick={() => setAlternatesOpen((o) => !o)}
                    >
                      {alternatesOpen ? <ChevronUp className='h-3.5 w-3.5' strokeWidth={1.5} /> : <ChevronDown className='h-3.5 w-3.5' strokeWidth={1.5} />}
                      Other options ({aiSuggestResponse.alternates.length})
                    </button>
                    {alternatesOpen && aiSuggestResponse.alternates.map((alt, i) => {
                      const altLocked = alt.accessible === false;
                      return (
                        <div key={alt.factor_id || alt.combo_id || i} className={`flex flex-col gap-1 rounded-md border p-2 ${altLocked ? 'border-warning/30 bg-warning-bg/5' : 'border-border bg-background'}`}>
                          <div className='flex items-center gap-1.5'>
                            <p className={`text-sm flex-1 min-w-0 ${altLocked ? 'text-muted-foreground' : 'text-foreground'}`}>{alt.factor_display_name || alt.factor_name}</p>
                            {altLocked && <Lock className='h-3.5 w-3.5 text-warning shrink-0' strokeWidth={1.5} />}
                          </div>
                          {alt.entities?.length > 0 && (
                            <p className='text-xs text-muted-foreground'>
                              {alt.entities.map((e) => e.name).join(', ')}
                            </p>
                          )}
                          {altLocked ? (
                            <div className='flex items-center gap-2'>
                              <p className='text-xs text-muted-foreground'>Requires data add-on</p>
                              {alt.required_add_on_id ? (
                                <button
                                  type='button'
                                  className='text-xs text-primary hover:underline'
                                  onClick={() => openUpgradeDialog(alt.required_add_on_id, alt.add_on_name)}
                                >
                                  Upgrade
                                </button>
                              ) : (
                                <button
                                  type='button'
                                  className='text-xs text-primary hover:underline'
                                  onClick={() => {
                                    const factorName = alt.factor_display_name || alt.factor_name || '';
                                    const subject = encodeURIComponent('Data add-on upgrade request');
                                    const body = encodeURIComponent(
                                      `Hi,\n\nI'd like to enquire about upgrading my account.\n\nFactor: ${factorName}\n\nThank you`,
                                    );
                                    window.open(`mailto:support@compareyourfootprint.com?subject=${subject}&body=${body}`);
                                  }}
                                >
                                  Enquire
                                </button>
                              )}
                            </div>
                          ) : (
                            <Button variant='outline' size='sm' className='self-start' onClick={() => handleSelectCandidate(alt)}>
                              Use this instead
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {aiSuggestResponse.suggest_status === 'no_match' && (
                  <div className='rounded-md bg-warning-bg/30 px-4 py-3 text-center'>
                    <p className='text-sm text-warning'>No match found. Try rephrasing the activity description above and suggesting again.</p>
                  </div>
                )}

                {!aiSuggestResponse.best && aiSuggestResponse.suggest_status !== 'no_match' && (
                  <p className='text-sm text-muted-foreground'>No strong match found. Try different wording above and click "Suggest factor" again.</p>
                )}
              </>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>

      <Dialog open={!!splitModal && !splitInlineRow} onOpenChange={(open) => !open && setSplitModal(null)}>
        <DialogContent className='sm:max-w-lg bg-popover text-popover-foreground'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2 text-foreground'>
              <Scissors size={16} strokeWidth={1.5} />
              Split into line items
            </DialogTitle>
          </DialogHeader>
          <DialogBody className='flex flex-col gap-4'>
            {splitModal && (
              <div className='rounded-md bg-muted/50 px-3 py-2'>
                <p className='text-sm font-medium text-foreground'>{splitModal.cleanDescription || splitModal.rawDescription}</p>
                <p className='text-xs text-muted-foreground'>Total: {splitModal.currency || '£'}{Number(splitModal.amount || 0).toFixed(2)}</p>
              </div>
            )}
            <p className='text-xs text-muted-foreground'>
              Break this transaction into multiple sub-items for more precise factor matching. Sub-row amounts must equal the original.
            </p>
            <div className='flex flex-col gap-2'>
              {splitLines.map((line, i) => (
                <div key={i} className='grid grid-cols-[1fr_90px_32px] gap-2 items-center'>
                  <Input
                    className='text-xs bg-background'
                    placeholder='Description (e.g. A4 paper)'
                    value={line.description}
                    onChange={(e) => setSplitLines((prev) => prev.map((l, j) => j === i ? { ...l, description: e.target.value } : l))}
                  />
                  <Input
                    className='text-xs bg-background text-right font-mono'
                    placeholder='Amount'
                    type='number'
                    step='0.01'
                    value={line.amount}
                    onChange={(e) => setSplitLines((prev) => prev.map((l, j) => j === i ? { ...l, amount: e.target.value } : l))}
                  />
                  {splitLines.length > 2 && (
                    <Button
                      variant='ghost'
                      size='sm'
                      className='h-7 w-7 p-0 text-danger'
                      onClick={() => setSplitLines((prev) => prev.filter((_, j) => j !== i))}
                    >
                      <Trash2 size={13} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button variant='outline' size='sm' className='self-start text-xs' onClick={() => setSplitLines((prev) => [...prev, { description: '', amount: '', date: '' }])}>
              <Plus size={13} strokeWidth={1.5} />Add line item
            </Button>
            <div className='flex items-center justify-between px-3 py-2 rounded-md bg-muted/30 text-sm font-semibold'>
              <span>Total: {splitModal?.currency || '£'}{splitTotal.toFixed(2)} of {splitModal?.currency || '£'}{Number(splitModal?.amount || 0).toFixed(2)}</span>
              {Math.abs(splitTotal - Number(splitModal?.amount || 0)) < 0.02 ? (
                <span className='text-xs text-success'>✓ Balanced</span>
              ) : (
                <span className='text-xs text-danger'>
                  {splitTotal > Number(splitModal?.amount || 0) ? 'Over' : 'Under'} by {splitModal?.currency || '£'}{Math.abs(splitTotal - Number(splitModal?.amount || 0)).toFixed(2)}
                </span>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant='outline' onClick={() => setSplitModal(null)}>Cancel</Button>
            <Button
              onClick={handleSplitConfirm}
              disabled={splitLines.filter((l) => l.description.trim() && l.amount).length < 2 || Math.abs(splitTotal - Number(splitModal?.amount || 0)) >= 0.02}
              loading={loading?.split}
            >
              <Scissors size={14} strokeWidth={1.5} />Confirm Split
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!entityPickerRow} onOpenChange={(open) => !open && setEntityPickerRow(null)}>
        <DialogContent className='sm:max-w-sm bg-popover text-popover-foreground'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2 text-foreground'>
              <Shield size={16} strokeWidth={1.5} />
              {entityPickerRow?.selectOnly ? 'Choose report category' : 'Confirm factor & category'}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className='flex flex-col gap-3'>
            <p className='text-sm text-muted-foreground'>
              {entityPickerRow?.selectOnly
                ? 'Pick which activity category this transaction should appear under in your reports. You can change this before you confirm the row.'
                : 'This factor belongs to multiple activity categories. Pick one, then we will confirm the match.'}
            </p>
            <p className='text-sm font-medium text-foreground truncate' title={entityPickerRow?.match?.factorName}>
              {entityPickerRow?.match?.factorName}
            </p>
            <div className='flex flex-col gap-1.5'>
              {(entityPickerRow?.match?.entities || []).map((entity) => {
                const rowId = entityPickerRow?.row?.id || entityPickerRow?.row?.stagingId;
                const isSelected = String(selectedEntities[rowId]) === String(entity.id);
                return (
                  <Button
                    key={entity.id}
                    variant={isSelected ? 'default' : 'outline'}
                    size='sm'
                    className='justify-start text-left'
                    onClick={() => setSelectedEntities((prev) => ({ ...prev, [rowId]: entity.id }))}
                  >
                    {entity.name}
                  </Button>
                );
              })}
            </div>
            {entityPickerRow?.fromAiSuggest && searchModalActivityError ? (
              <p className='text-xs text-destructive'>{searchModalActivityError}</p>
            ) : null}
          </DialogBody>
          <DialogFooter>
            <Button variant='outline' size='sm' onClick={() => setEntityPickerRow(null)}>Cancel</Button>
            {entityPickerRow?.selectOnly ? (
              <Button
                size='sm'
                onClick={() => setEntityPickerRow(null)}
              >
                <Check size={14} strokeWidth={1.5} />
                Save category choice
              </Button>
            ) : (
            <Button
              size='sm'
              onClick={async () => {
                const epRow = entityPickerRow;
                const rowId = epRow.row.id || epRow.row.stagingId;
                const entityId = selectedEntities[rowId];

                if (epRow.fromAiSuggest) {
                  const matchLike = epRow.candidate ? candidateToMatch(epRow.candidate, epRow.row) : epRow.match;
                  if (!isSpendFactor(matchLike, metricsById)) {
                    const eff = getEffectiveActivityDetectedAmount(matchLike, epRow.row, metricsById);
                    const amt = (searchModalActivity || '').trim() || (eff != null && eff !== '' ? String(eff) : '');
                    if (!amt || Number.isNaN(parseFloat(amt))) {
                      setSearchModalActivityError('Enter activity amount for this factor before confirming.');
                      return;
                    }
                  }
                  const factorId = matchLike.factorId || matchLike.factor_id;
                  const factorName = matchLike.factorName || matchLike.factor_name;
                  const activityOverride = (searchModalActivity || '').trim() || undefined;
                  const ok = await handleConfirm(epRow.row, matchLike, rememberChoice, entityId, activityOverride);
                  if (ok !== true) {
                    setSearchModalActivityError('Enter activity amount for this factor before confirming.');
                    return;
                  }

                  setEntityPickerRow(null);

                  if (factorId && searchQuery?.trim() && submitFactorSuggestFeedback) {
                    const cr = Object.fromEntries(Object.entries(clarifyReplies).filter(([, v]) => v != null && String(v).trim() !== ''));
                    const alternatesShown = aiSuggestResponse?.alternates?.map((a) => a.factor_id).filter(Boolean);
                    submitFactorSuggestFeedback({
                      text: searchQuery.trim(),
                      factor_id: factorId,
                      factor_name: factorName,
                      org_country: orgCountry,
                      clarify_replies: Object.keys(cr).length > 0 ? cr : undefined,
                      remember_choice: rememberChoice,
                      alternates_shown: alternatesShown?.length > 0 ? alternatesShown : undefined,
                      confidence_score: epRow.candidate?.signals?.llm_confidence ?? undefined,
                      clarify_rounds: aiSuggestResponse?.clarify_round ?? undefined,
                    }).catch(() => {});
                  }

                  setSearchModal(null);
                  setSearchQuery('');
                  setSearchModalActivity('');
                  setAiSuggestResponse(null);
                  setClarifyReplies({});
                  setAlternatesOpen(false);
                  setLocationOverride(null);
                } else {
                  setEntityPickerRow(null);
                  await handleConfirm(epRow.row, epRow.match, rememberChoice, entityId);
                }
              }}
            >
              <Check size={14} strokeWidth={1.5} />
              Confirm with Selection
            </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!upgradeDialog} onOpenChange={(open) => !open && setUpgradeDialog(null)}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>Upgrade to Premium Dataset</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {upgradeDialog && (() => {
              const product = findProductForAddOn(upgradeDialog.addOnId);
              const formatMinor = (v, ccy) => {
                if (v == null) return null;
                const sym = { gbp: '£', usd: '$', eur: '€' }[ccy] || '';
                return `${sym}${(v / 100).toFixed(2)}`;
              };
              return (
                <div className='space-y-4'>
                  <div>
                    <p className='text-sm font-semibold text-foreground'>
                      {product?.name || upgradeDialog.addOnName || 'Premium Add-On'}
                    </p>
                    {product?.description && (
                      <p className='text-xs text-muted-foreground mt-1'>{product.description}</p>
                    )}
                    {product?.addon_ids?.length > 1 && (
                      <p className='text-xs text-info mt-1'>
                        This bundle includes {product.addon_ids.length} datasets.
                      </p>
                    )}
                  </div>
                  {addonCatalogLoading && (
                    <p className='text-xs text-muted-foreground'>Loading pricing...</p>
                  )}
                  {product && (
                    <div className='space-y-2'>
                      <p className='text-xs font-medium text-muted-foreground uppercase tracking-wider'>Billing Type</p>
                      <div className='grid grid-cols-2 gap-2'>
                        {product.price_annual != null && (
                          <button
                            type='button'
                            className={`rounded-lg border p-3 text-left transition-colors ${
                              upgradeBillingType === 'annual'
                                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                                : 'border-border hover:border-primary/30'
                            }`}
                            onClick={() => setUpgradeBillingType('annual')}
                          >
                            <p className='text-sm font-semibold text-foreground'>Annual</p>
                            <p className='text-xs text-muted-foreground'>
                              {formatMinor(product.price_annual, product.currency)}/yr
                            </p>
                          </button>
                        )}
                        {product.price_monthly != null && (
                          <button
                            type='button'
                            className={`rounded-lg border p-3 text-left transition-colors ${
                              upgradeBillingType === 'monthly'
                                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                                : 'border-border hover:border-primary/30'
                            }`}
                            onClick={() => setUpgradeBillingType('monthly')}
                          >
                            <p className='text-sm font-semibold text-foreground'>Monthly</p>
                            <p className='text-xs text-muted-foreground'>
                              {formatMinor(product.price_monthly, product.currency)}/mo
                            </p>
                            <p className='text-[10px] text-muted-foreground mt-1'>Minimum commitment applies</p>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {!product && !addonCatalogLoading && (
                    <Alert>
                      <AlertDescription className='text-xs'>
                        Pricing details are not available for this add-on. Please contact support for more information.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              );
            })()}
          </DialogBody>
          <DialogFooter>
            <Button variant='outline' onClick={() => setUpgradeDialog(null)}>Cancel</Button>
            {findProductForAddOn(upgradeDialog?.addOnId) ? (
              <Button onClick={handleUpgradePurchase} loading={addonPurchaseLoading}>
                Confirm Purchase
              </Button>
            ) : !addonCatalogLoading ? (
              <Button
                variant='outline'
                onClick={() => {
                  const name = upgradeDialog?.addOnName || '';
                  const id = upgradeDialog?.addOnId || '';
                  const subject = encodeURIComponent('Data add-on upgrade request');
                  const body = encodeURIComponent(
                    `Hi,\n\nI'd like to enquire about the ${name} add-on (ID: ${id}).\n\nThank you`,
                  );
                  window.open(`mailto:support@compareyourfootprint.com?subject=${subject}&body=${body}`);
                }}
              >
                Contact Support
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

const mapStateToProps = (state) => {
  const orgDetails = state.organisation?.details || {};
  const countryId = orgDetails.country;
  const orgCountry = countryId
    ? (state.organisation?.countries || []).find((c) => String(c.id) === String(countryId))?.countryAlpha2Code
    : undefined;
  return {
    factorMatches: state.intelligentImport?.factorMatches || [],
    stagingItems: state.intelligentImport?.stagingItems || [],
    loading: state.intelligentImport?.loading || {},
    orgCountry,
    currentJob: state.intelligentImport?.currentJob || null,
    orgMetrics: state.organisation?.metrics || [],
    orgFacilities: state.facility?.list || [],
    cyfAdmin: state.profile?.details?.cyfAdmin === true,
    whiteLabelAdmin: state.profile?.details?.whiteLabelAdmin === true,
    addonCatalog: state.payment?.addonCatalog || [],
    addonCatalogLoading: state.payment?.addonCatalogLoading || false,
    addonPurchaseLoading: state.payment?.addonPurchaseLoading || false,
  };
};

const mapDispatchToProps = {
  getFactorMatches: IMPORT_ACTIONS.getFactorMatches,
  getStagingItems: IMPORT_ACTIONS.getStagingItems,
  confirmMatch: IMPORT_ACTIONS.confirmMatch,
  rejectMatch: IMPORT_ACTIONS.rejectMatch,
  unconfirmMatch: IMPORT_ACTIONS.unconfirmMatch,
  bulkConfirmHighConfidence: IMPORT_ACTIONS.bulkConfirmHighConfidence,
  commitImport: IMPORT_ACTIONS.commitImport,
  recordAuditEvent: IMPORT_ACTIONS.recordAuditEvent,
  excludeRow: IMPORT_ACTIONS.excludeRow,
  searchFactors: IMPORT_ACTIONS.searchFactors,
  splitStagingRow: IMPORT_ACTIONS.splitStagingRow,
  retryMatch: IMPORT_ACTIONS.retryMatch,
  overrideDedupSkip: IMPORT_ACTIONS.overrideDedupSkip,
  suggestFactorFromText: USAGE_ACTIONS.suggestFactorFromText,
  submitFactorSuggestFeedback: USAGE_ACTIONS.submitFactorSuggestFeedback,
  getAddonCatalog: PAYMENT_ACTIONS.getAddonCatalog,
  purchaseAddOn: PAYMENT_ACTIONS.purchaseAddOn,
};

export default connect(mapStateToProps, mapDispatchToProps)(FactorMatchReview);
